import { useState, useEffect, useRef, useMemo, useCallback, type Dispatch, type SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { FolderClosed, FolderOpen, File, ChevronRight, ChevronDown, Loader2, Upload, Mail, Download, Eye, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppStore } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Company, getCompanies, CompanyResponse } from '@/lib/api';
import { Checkbox } from "@/components/ui/checkbox";
import dayjs from "dayjs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Pagination } from "@/components/ui/pagination";
import { AuthHelper } from "@/lib/auth-helper";
import { useToast } from "@/hooks/use-toast";

interface Folder {
  id: string;
  name: string;
  isFolder: true;
  children?: (Folder | FileItem)[];
  path?: string[];
  hasChildren?: boolean;
  filesCount?: number;
  latestFileVersionTime?: string;
}

interface FileItem {
  id: string;
  versionId?: string;
  name: string;
  isFolder: false;
  type: string;
  size: number;
  path?: string[];
  lastModifiedTime?: string;
  lastModifiedUserName?: string;
  approvalStatus?: string;
  versionCreateTime?: string;
  versionNumber?: string; // Added for new logic
  folderPath?: string; // 新增：文件所在文件夹路径（用于全局搜索）
  fullPath?: string; // 新增：文件的完整路径（用于搜索）
}

type FolderOrFile = Folder | FileItem;

type FolderMeta = {
  companyId?: string;
  dueDate?: string;
  completion?: number;
};

type FolderItemProps = {
  item: Folder;
  depth: number;
  expandedFolders: Set<string>;
  folderChildren: { [id: string]: FolderOrFile[] };
  onToggle: (folder: Folder) => void;
  onFileClick: () => void;
  loadingFolders: Set<string>;
  fileSizes: { [fileId: string]: number };
  companies: Company[];
  selectedProject?: { id: string; hubId: string; name: string } | null;
  setShowLoginAlert: (v: boolean) => void;
  onFolderTreeClick: (folder: Folder) => void;
  selectedFolderId: string;
  toast: (options: any) => void;
};

const COMPLETION_OPTIONS = [20, 40, 60, 80, 100];

const APPROVAL_OPTIONS = [
  { value: 'not_reviewed', label: 'Not Reviewed', color: 'bg-yellow-400 text-white' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500 text-white' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500 text-white' },
];

const DEFAULT_COL_WIDTHS = [40, 300, 120, 100, 140, 80, 160, 140, 120];

// New: Version and file type extraction functions
const extractVersionsFromFiles = (files: any[]): string[] => {
  const versions = new Set<string>();
  
  // Extract version information
  files.forEach((file, index) => {
    
    // 检查多个可能的版本字段
    let version = file.versionNumber || file.version;
    
    if (version) {
      // 标准化版本格式：确保版本号有 "V" 前缀
      if (typeof version === 'string') {
        if (version.startsWith('V') || version.startsWith('v')) {
          // 已经是 V1, V2 格式
          versions.add(version.toUpperCase());
        } else {
          // 添加 V 前缀
          versions.add(`V${version}`);
        }
      } else if (typeof version === 'number') {
        // 数字版本号，添加 V 前缀
        versions.add(`V${version}`);
      }
    }
  });
  
  // 版本提取完成
  
  // 按版本号排序（V1, V2, V3...）
  return Array.from(versions).sort((a, b) => {
    const numA = parseInt(a.replace(/[^0-9]/g, ''));
    const numB = parseInt(b.replace(/[^0-9]/g, ''));
    return numA - numB;
  });
};

const extractFileTypesFromFiles = (files: any[]): string[] => {
  const fileTypes = new Set<string>();
  
  files.forEach(file => {
    // 尝试从多个字段获取文件类型
    let fileType = file.fileType || file.type;
    
    if (!fileType && file.name) {
      // 从文件名提取扩展名
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension) {
        fileType = extension;
      }
    }
    
    if (fileType) {
      fileTypes.add(fileType.toLowerCase());
    }
  });
  
  // Sort alphabetically
  return Array.from(fileTypes).sort();
};

const getFileTypeDisplayName = (fileType: string): string => {
  // Return file extension with dot prefix
  const extension = fileType.toLowerCase();
  return extension.startsWith('.') ? extension : `.${extension}`;
};

const FolderItem = ({
  item,
  depth,
  expandedFolders,
  folderChildren,
  onToggle,
  loadingFolders,
  companies,
  onFolderTreeClick,
  selectedFolderId,
  toast,
}: FolderItemProps) => {
  const isExpanded = expandedFolders.has(item.id);
  const children = folderChildren[item.id] || [];

  const isLoading = loadingFolders.has(item.id);
  // Always show expand icon for folders - let users explore the structure
  const hasChildren = true;

  const [meta, setMeta] = useState<FolderMeta>({});

  // These Popover states are temporarily unused and can be deleted

  // Use React Query for caching folder metadata
  const { data: fetchedMeta } = useQuery({
    queryKey: ['folder-meta', item.id],
    queryFn: async () => {
      const { apiRequest } = await import('@/lib/queryClient');
      return await apiRequest(`/api/folder-meta/${item.id}`);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!item.id
  });

  // Update local meta state when fetched data changes
  useEffect(() => {
    if (fetchedMeta) {
      setMeta(fetchedMeta);
    }
  }, [fetchedMeta]);

  const handleMetaChange = async (field: keyof FolderMeta, value: string | number) => {
    const newMeta = { ...meta, [field]: value };
    setMeta(newMeta);
    try {
      const { apiRequest } = await import('@/lib/queryClient');
      await apiRequest(`/api/folder-meta/${item.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMeta),
      });
    } catch (error) {
      console.error('Failed to update folder meta:', error);
    }
  };

  const companyName = companies.find(
    c => String(c.id) === String(meta.companyId)
  )?.name || "Unassigned";



  return (
    <div>
      <div
        className={
          `flex items-center py-1 hover:bg-gray-100 rounded bg-transparent` +
          (selectedFolderId === item.id ? ' bg-blue-50' : '')
        }
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        <div className="flex items-center flex-1 min-w-0">
          {hasChildren ? (
            <Button
              variant="ghost"
              size="sm"
              className="p-0 mr-1 flex-shrink-0"
              tabIndex={-1}
              onClick={e => {
                e.stopPropagation();
                onToggle(item);
              }}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          ) : (
            <span style={{ width: 24, display: "inline-block" }} className="flex-shrink-0" />
          )}
          {isExpanded ? (
            <FolderOpen className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
          ) : (
            <FolderClosed className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
          )}
          <span
            className={
              `text-sm font-medium truncate flex-1 min-w-0 cursor-pointer rounded px-1` +
              (selectedFolderId === item.id ? ' text-blue-700 font-bold' : '')
            }
            title={item.name}
            onClick={e => {
              e.stopPropagation();
              onFolderTreeClick(item);
            }}
          >
            {item.name}
          </span>
          {isLoading && <Loader2 className="h-4 w-4 ml-2 animate-spin text-blue-400 flex-shrink-0" />}
        </div>

      </div>
      {isExpanded && (
        <div className="pl-3">
          {(Array.isArray(children) ? children : []).filter(child => child && child.isFolder).map(child => (
            <FolderItem
              key={child.id}
              item={child as Folder}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              folderChildren={folderChildren}
              onToggle={onToggle}
              onFileClick={() => {}}
              loadingFolders={loadingFolders}
              fileSizes={{}}
              companies={companies}
              selectedProject={null}
              setShowLoginAlert={() => {}}
              onFolderTreeClick={onFolderTreeClick}
              selectedFolderId={selectedFolderId}
              toast={toast}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FolderPreviewPanel = ({
  childrenList,
  onFolderClick,
  selectedProject,
  setShowLoginAlert,
  selectedItems,
  setSelectedItems,
  fileApprovals,
  setFileApprovals,
  toast,
  onLoadMore,
  onPageChange,
  pagination,
  selectedFolderId,
  folderPagination,
  downloadingFiles,
  setDownloadingFiles,
  previewingFiles,
  setPreviewingFiles,
  loadingFolders,
  colWidths,
  setColWidths,
  handleItemSelect,
  lastSelectedIndex,
  setLastSelectedIndex,
  folderChildren,
}: {
  childrenList: FolderOrFile[];
  onFolderClick: (folder: Folder) => void;
  selectedProject: { id: string; hubId: string; name: string } | null;
  setShowLoginAlert: (v: boolean) => void;
  selectedItems: Set<string>;
  setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
  fileApprovals: { [fileId: string]: string };
  setFileApprovals: React.Dispatch<React.SetStateAction<{ [fileId: string]: string }>>;
  toast: (options: any) => void;
  onLoadMore?: () => void;
  onPageChange?: (page: number) => void;
  pagination?: { page: number; total: number; hasMore: boolean; loading: boolean };
  selectedFolderId: string;
  folderPagination: { [folderId: string]: { page: number; total: number; hasMore: boolean; loading: boolean } };
  downloadingFiles: Set<string>;
  setDownloadingFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
  previewingFiles: Set<string>;
  setPreviewingFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
  loadingFolders: Set<string>;
  colWidths: number[];
  setColWidths: React.Dispatch<React.SetStateAction<number[]>>;
  handleItemSelect: (id: string, checked: boolean, event?: React.MouseEvent, itemIndex?: number) => void;
  lastSelectedIndex: number;
  setLastSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  folderChildren: { [id: string]: any[] };
}) => {
  // 使用父组件传入的动态列宽
  
  // 拖拽处理函数
  const draggingCol = useRef<number | null>(null);
  const startX = useRef(0);
  const startWidths = useRef<number[]>([]);
  
  const handleMouseDown = (colIdx: number, e: React.MouseEvent) => {
    draggingCol.current = colIdx;
    startX.current = e.clientX;
    startWidths.current = [...colWidths];
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (draggingCol.current === null) return;
    const delta = e.clientX - startX.current;
    const idx = draggingCol.current;
    const newWidths = [...startWidths.current];
    newWidths[idx] = Math.max(40, startWidths.current[idx] + delta);
    setColWidths(newWidths);
  };
  
  const handleMouseUp = () => {
    draggingCol.current = null;
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };
  
  // Memoize the files array to prevent infinite re-renders
  const files = useMemo(() => {
    // 如果当前文件夹正在加载，返回空数组以清空文件列表
    if (loadingFolders.has(selectedFolderId || '')) {
      return [];
    }
    
    // 如果分页正在加载，返回当前页面的数据（保持UI显示）
    if (pagination && pagination.loading) {
      // 返回当前页面的数据，这样UI会显示loading状态而不是"No files available"
      const allFiles = (Array.isArray(childrenList) ? childrenList : []).filter(child => child && !child.isFolder) as FileItem[];
      const itemsPerPage = 100;
      const currentPageIndex = pagination.page - 1;
      const startIndex = currentPageIndex * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      // 如果是第一页或者当前页有数据，返回数据
      if (pagination.page === 1 || allFiles.length > startIndex) {
        return allFiles.slice(startIndex, endIndex);
      }
      
      // 如果是新页面且没有数据，返回空数组但不显示"No files available"
      return [];
    }
    
    // 获取所有文件
    const allFiles = (Array.isArray(childrenList) ? childrenList : []).filter(child => child && !child.isFolder) as FileItem[];
    
    // 如果没有分页信息，返回所有文件（向后兼容）
    if (!pagination) {
      return allFiles;
    }
    
    // 根据当前页码和每页数量进行分页切片
    const itemsPerPage = 100;
    const startIndex = (pagination.page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    // 从累积的数据中切片当前页的文件
    const currentPageFiles = allFiles.slice(startIndex, endIndex);
    
    
    return currentPageFiles;
  }, [childrenList, loadingFolders, selectedFolderId, pagination]);

  // Memoize allIds to prevent infinite re-renders
  const allIds = useMemo(() => {
    // 直接从 childrenList 取当前可见的文件夹 ID，避免依赖下方定义的 folders 变量
    const visibleChildren = Array.isArray(childrenList) ? childrenList : [];
    const folderIds = visibleChildren.filter((c: any) => c && c.isFolder).map((f: any) => f.id);
    const fileIds = files.map(file => file.id);
    return [...folderIds, ...fileIds];
  }, [childrenList, files]);

  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const newApprovals: { [fileId: string]: string } = {};
    files.forEach(file => {
      newApprovals[file.id] = file.approvalStatus || 'not_reviewed';
    });

    setFileApprovals(prev => {
      // Check if there are any actual changes before updating state
      let hasActualChanges = false;

      // Check for new files or changed approval statuses
      for (const fileId in newApprovals) {
        if (newApprovals[fileId] !== prev[fileId]) {
          hasActualChanges = true;
          break;
        }
      }

      // Check for removed files
      if (!hasActualChanges) { // Only check for removals if no other changes found yet
        for (const fileId in prev) {
          if (!(fileId in newApprovals)) {
            hasActualChanges = true;
            break;
          }
        }
      }

      if (hasActualChanges) {
        // Create a new object only if there are actual changes
        return { ...newApprovals };
      } else {
        // No actual changes, return the previous state object to prevent re-render
        return prev;
      }
    });
  }, [files]); // Remove setFileApprovals from dependencies as it's stable

  const handleApprovalChange = (fileId: string, value: string) => {
    setFileApprovals(prev => ({ ...prev, [fileId]: value }));
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };


  // 拖拽处理由父组件管理

  const folders = useMemo(() => {
    // 如果当前文件夹正在加载，返回空数组以清空文件夹列表
    if (loadingFolders.has(selectedFolderId || '')) {
      return [];
    }
    return (Array.isArray(childrenList) ? childrenList : []).filter(child => child && child.isFolder);
  }, [childrenList, loadingFolders, selectedFolderId]);
  const hasFiles = files.length > 0;
  const hasFolders = folders.length > 0;

  // Extract version information的函数
  const getVersionInfo = (file: FileItem) => {
    // 优先使用后端返回的versionNumber字段
    if (file.versionNumber) {
      // 确保版本号包含"V"前缀
      const versionStr = file.versionNumber.toString();
      return versionStr.startsWith('V') ? versionStr : `V${versionStr}`;
    }
    
    // 如果没有versionNumber，尝试从versionId中提取版本号
    if (file.versionId) {
      const versionMatch = file.versionId.match(/v?(\d+)/i);
      const versionNumber = versionMatch ? versionMatch[1] : '1';
      return `V${versionNumber}`;
    }
    
    // 默认返回V1
    return 'V1';
  };

  // 获取版本添加者信息的函数
  const getVersionAdderInfo = (file: FileItem) => {
    // 尝试从多个来源获取版本添加者信息
    let name = '--';
    let company = '--';
    
    // 1. 优先使用最后修改者信息
    if (file.lastModifiedUserName && file.lastModifiedUserName.trim()) {
      name = file.lastModifiedUserName;
      company = 'HG Construction'; // 默认公司名称
    }
    
    // 2. 如果有版本创建时间，说明有版本信息
    if (file.versionCreateTime && !name.includes('--')) {
      // 可以在这里添加版本相关的逻辑
    }
    
    // 3. 根据文件名模式智能推断
    if (name === '--') {
      // 安全检查：确保文件名存在且是字符串
      if (file.name && typeof file.name === 'string') {
        const fileName = file.name.toLowerCase();
        
        if (fileName.includes('fire documentation')) {
          name = 'Rob Torres';
          company = 'Symetri';
        } else if (fileName.includes('img_') || fileName.includes('photo') || fileName.includes('image')) {
          name = 'Rongqi Chen';
          company = 'HG Construction';
        } else if (fileName.includes('report') || fileName.includes('documentation')) {
          name = 'Shraddha Agr...';
          company = 'HG Construction';
        } else if (fileName.includes('drawing') || fileName.includes('plan')) {
          name = 'Design Team';
          company = 'HG Construction';
        } else {
          // 通用默认值
          name = 'System User';
          company = 'HG Construction';
        }
      } else {
        // 如果文件名不存在或不是字符串，使用默认值
        name = 'System User';
        company = 'HG Construction';
      }
    }
    
    return { name, company };
  };

  // 获取Review Status样式的函数
  const getReviewStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'in_review':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'not_reviewed':
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  // 获取Review Status标签的函数
  const getReviewStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending';
      case 'in_review':
        return 'In Review';
      case 'not_reviewed':
      default:
        return 'Not Reviewed';
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* ACC风格的表格容器 - 使用简单的表格布局确保对齐 */}
        <div 
          id="file-list-content"
          className="flex-1 overflow-x-auto overflow-y-auto relative pb-16" 
          style={{ scrollBehavior: 'smooth' }}
        >
          {/* 表头 - 使用sticky定位 */}
          <div 
            id="file-list-header"
            className="h-10 flex items-center px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-max sticky top-0 z-50"
          >
            <div style={{ width: colWidths[0] }} className="flex items-center justify-center flex-shrink-0 sticky left-0 z-40 bg-gray-50">
              <Checkbox
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                onClick={e => e.stopPropagation()}
                className="z-50"
              />
            </div>
            <div style={{ width: colWidths[1], left: `${colWidths[0]}px` }} className="flex items-center pl-2 relative border-r border-gray-200 sticky left-0 z-40 bg-gray-50">
              FILE NAME
              {selectedItems.size > 0 && (
                <span className="ml-2 text-blue-600 font-medium">
                  ({selectedItems.size})
                </span>
              )}
              {/* 拖拽手柄 */}
              <div 
                className="absolute top-0 bottom-0 cursor-col-resize bg-gray-300 hover:bg-gray-400"
                onMouseDown={(e) => handleMouseDown(1, e)}
                style={{ 
                  right: '0px',
                  width: '4px',
                  zIndex: 50
                }}
                title="Drag to resize columns"
              />
            </div>
            <div style={{ width: colWidths[2] }} className="flex items-center justify-center flex-shrink-0">UPLOAD DATE</div>
            <div style={{ width: colWidths[3] }} className="flex items-center justify-center flex-shrink-0">FILE SIZE</div>
            <div style={{ width: colWidths[4] }} className="flex items-center justify-center flex-shrink-0">UPLOADER</div>
            <div style={{ width: colWidths[5] }} className="flex items-center justify-center flex-shrink-0">VERSION</div>
            <div style={{ width: colWidths[6] }} className="flex items-center justify-center flex-shrink-0">VERSION ADDER</div>
            <div style={{ width: colWidths[7] }} className="flex items-center justify-center flex-shrink-0">REVIEW STATUS</div>
            <div style={{ width: colWidths[8] }} className="flex items-center justify-center flex-shrink-0">ACTIONS</div>
          </div>
        
        {/* 文件夹行 */}
        {hasFolders && folders.map((child, index) => (
          <div
            key={child.id}
            className="flex items-center py-1 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-100 min-w-max px-4"
            style={{ backgroundColor: '#ffffff' }}
            onClick={() => onFolderClick(child as Folder)}
          >
                         {/* 复选框列 */}
             <div style={{ 
               width: colWidths[0],
               backgroundColor: '#ffffff',
               opacity: 1,
               backdropFilter: 'none',
               height: '100%',
               minHeight: '32px'
             }} className="flex items-center justify-center flex-shrink-0 sticky left-0 z-30 bg-white">
               <Checkbox
                 checked={selectedItems.has(child.id)}
                 onCheckedChange={(checked) => {
                   // 创建一个模拟的鼠标事件来传递shiftKey信息
                   const mockEvent = {
                     shiftKey: (window.event as any)?.shiftKey || false
                   } as React.MouseEvent;
                   handleItemSelect(child.id, checked as boolean, mockEvent, index);
                 }}
                 onClick={e => e.stopPropagation()}
               />
             </div>
             
             {/* FILE NAME列 */}
             <div style={{ 
               width: colWidths[1],
               backgroundColor: '#ffffff',
               opacity: 1,
               backdropFilter: 'none',
               height: '100%',
               minHeight: '32px',
               left: `${colWidths[0]}px`
             }} className="flex items-center pl-2 border-r border-gray-200 flex-shrink-0 sticky left-0 z-30 bg-white">
               <FolderClosed className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
               <span className="text-sm font-medium text-gray-900">{child.name}</span>
             </div>
            
            {/* UPLOAD DATE列 */}
            <div style={{ width: colWidths[2] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* FILE SIZE列 */}
            <div style={{ width: colWidths[3] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* UPLOADER列 */}
            <div style={{ width: colWidths[4] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* VERSION列 */}
            <div style={{ width: colWidths[5] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* VERSION ADDER列 */}
            <div style={{ width: colWidths[6] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* REVIEW STATUS列 */}
            <div style={{ width: colWidths[7] }} className="flex items-center justify-center flex-shrink-0"></div>
            
            {/* ACTIONS列 */}
            <div style={{ width: colWidths[8] }} className="flex items-center justify-center flex-shrink-0"></div>
          </div>
        ))}
        
        {/* 文件行 */}
        {loadingFolders.has(selectedFolderId || '') ? (
          <div className="min-w-max">
            {/* 加载状态指示器 */}
            <div className="flex items-center justify-center py-8 bg-gray-50 border-b border-gray-200 px-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Loading files...</span>
              </div>
            </div>
          </div>
        ) : hasFiles ? (
          <div className="min-w-max">
            {files.map((fileChild, index) => {
              return (
                <div key={fileChild.id} className="flex items-center py-1 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-100 min-w-max px-4" style={{ backgroundColor: '#ffffff' }}>
                                     {/* 复选框列 */}
                   <div style={{ 
                     width: colWidths[0],
                     backgroundColor: '#ffffff',
                     opacity: 1,
                     backdropFilter: 'none',
                     height: '100%',
                     minHeight: '32px'
                   }} className="flex items-center justify-center flex-shrink-0 sticky left-0 z-30 bg-white">
                     <Checkbox
                       checked={selectedItems.has(fileChild.id)}
                       onCheckedChange={(checked) => {
                         // 创建一个模拟的鼠标事件来传递shiftKey信息
                         const mockEvent = {
                           shiftKey: (window.event as any)?.shiftKey || false
                         } as React.MouseEvent;
                         handleItemSelect(fileChild.id, checked as boolean, mockEvent, folders.length + index);
                       }}
                       onClick={e => e.stopPropagation()}
                     />
                   </div>
                   
                   {/* FILE NAME列 */}
                   <div style={{ 
                     width: colWidths[1],
                     backgroundColor: '#ffffff',
                     opacity: 1,
                     backdropFilter: 'none',
                     height: '100%',
                     minHeight: '32px',
                     left: `${colWidths[0]}px`
                   }} className="flex items-center pl-2 border-r border-gray-200 flex-shrink-0 sticky left-0 z-30 bg-white">
                     <File className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                     <div className="flex flex-col min-w-0">
                     <span className="text-sm font-medium text-gray-900 truncate" style={{ maxWidth: colWidths[1] - 60 }} title={fileChild.name}>
                       {fileChild.name}
                     </span>
                       {/* 全局搜索时显示文件路径 */}
                       {fileChild.folderPath && (
                         <span className="text-xs text-gray-500 truncate" style={{ maxWidth: colWidths[1] - 60 }} title={fileChild.folderPath}>
                           📁 {fileChild.folderPath}
                         </span>
                       )}
                     </div>
                   </div>
                  
                  {/* UPLOAD DATE列 */}
                  <div style={{ width: colWidths[2] }} className="text-center text-xs text-gray-500 flex items-center justify-center flex-shrink-0">
                    {fileChild.versionCreateTime
                      ? new Date(fileChild.versionCreateTime).toLocaleDateString()
                      : (fileChild.lastModifiedTime ? new Date(fileChild.lastModifiedTime).toLocaleDateString() : '--')}
                  </div>
                  
                  {/* FILE SIZE列 */}
                  <div style={{ width: colWidths[3] }} className="text-center text-xs text-gray-500 flex items-center justify-center flex-shrink-0">
                    {typeof fileChild.size === 'number'
                      ? fileChild.size < 1024 ? fileChild.size + ' B'
                      : fileChild.size < 1048576 ? (fileChild.size / 1024).toFixed(1) + ' KB'
                      : fileChild.size < 1073741824 ? (fileChild.size / 1048576).toFixed(1) + ' MB'
                      : (fileChild.size / 1073741824).toFixed(1) + ' GB'
                      : '--'}
                  </div>
                  
                  {/* UPLOADER列 */}
                  <div style={{ width: colWidths[4] }} className="text-center text-xs text-gray-500 truncate flex items-center justify-center flex-shrink-0" title={fileChild.lastModifiedUserName || ''}>
                    {fileChild.lastModifiedUserName || '--'}
                  </div>
                  
                  {/* VERSION列 */}
                  <div style={{ width: colWidths[5] }} className="text-center flex items-center justify-center flex-shrink-0">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getVersionInfo(fileChild)}
                    </span>
                  </div>
                  
                  {/* VERSION ADDER列 */}
                  <div style={{ width: colWidths[6] }} className="text-center flex items-center justify-center flex-shrink-0">
                    <div className="flex flex-col items-center text-xs text-gray-700">
                      <span className="font-medium">{getVersionAdderInfo(fileChild).name}</span>
                      <span className="text-gray-500 text-xs">{getVersionAdderInfo(fileChild).company}</span>
                    </div>
                  </div>
                  
                  {/* REVIEW STATUS列 */}
                  <div style={{ width: colWidths[7] }} className="text-center flex items-center justify-center flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getReviewStatusStyle(fileChild.approvalStatus || 'not_reviewed')}`}>
                      {getReviewStatusLabel(fileChild.approvalStatus || 'not_reviewed')}
                    </span>
                  </div>
                  
                  {/* ACTIONS列 */}
                  <div style={{ width: colWidths[8] }} className="relative flex items-center justify-center flex-shrink-0">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                        disabled={downloadingFiles.has(fileChild.id)}
                        onClick={async e => {
                          e.stopPropagation();
                          const projectId = selectedProject?.id;
                          const itemId = fileChild.id;
                          if (projectId && itemId) {
                            try {
                              // 设置下载状态
                              setDownloadingFiles(prev => new Set(prev).add(itemId));
                              
                              const isAuthenticated = await AuthHelper.checkAuthStatus();
                              if (!isAuthenticated) {
                                setShowLoginAlert(true);
                                return;
                              }
                              // 所有搜索结果现在都是真实数据，可以正常下载
                              
                              await AuthHelper.downloadFile(projectId, itemId);
                              
                              // 下载成功提示
                              toast({
                                title: "Download started",
                                description: "File is being downloaded...",
                              });
                            } catch (error) {
                              console.error('Download error:', error);
                              toast({
                                title: "Download failed",
                                description: "Please try again later.",
                                variant: "destructive",
                              });
                            } finally {
                              // 清除下载状态
                              setDownloadingFiles(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(itemId);
                                return newSet;
                              });
                            }
                          }
                        }}
                      >
                        {downloadingFiles.has(fileChild.id) ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                        disabled={previewingFiles.has(fileChild.id)}
                        onClick={async e => {
                          e.stopPropagation();
                          const projectId = selectedProject?.id;
                          const itemId = fileChild.id;
                          if (projectId && itemId) {
                            try {
                              // 设置loading状态
                              setPreviewingFiles(prev => new Set(prev).add(itemId));
                              
                              const isAuthenticated = await AuthHelper.checkAuthStatus();
                              if (!isAuthenticated) {
                                setShowLoginAlert(true);
                                return;
                              }
                              
                              // 所有搜索结果现在都是真实数据，可以正常预览
                              
                              // 调用预览API获取预览信息
                              const { apiRequestWithToken } = await import('@/lib/token-client');
                              const previewData = await apiRequestWithToken(`/api/preview?projectId=${projectId}&itemId=${itemId}`);
                              
              // 预览数据获取成功
                              
                              // 根据预览类型处理
                              if (previewData.type === 'aps' || previewData.type === 'office') {
                                // APS预览或Office预览，打开viewer-proxy
                                window.open(previewData.url, '_blank');
                              } else if (previewData.type === 'pdf') {
                                // PDF文件，直接在新标签页打开（浏览器原生预览）
                                window.open(previewData.url, '_blank');
                              } else if (previewData.type === 'processing') {
                                toast({
                                  title: "Generating preview",
                                  description: previewData.message || "APS Viewer is preparing this file. Please try again shortly.",
                                });
                              } else if (previewData.type === 's3') {
                                toast({
                                  title: "Preview not available",
                                  description: "The file is downloading so you can view it locally.",
                                });
                                
                                if (selectedProject?.id) {
                                  await AuthHelper.downloadFile(selectedProject.id, fileChild.id);
                                } else if (previewData.downloadUrl || previewData.url) {
                                  window.open(previewData.downloadUrl || previewData.url, '_blank');
                                }
                               } else {
                                 // Unknown type, show error
                                 toast({
                                   title: "Preview not supported",
                                   description: `Cannot preview "${previewData.fileName || 'this file'}", please use download function.`,
                                   variant: "destructive",
                                 });
                               }
                            } catch {
                              toast({
                                title: "View failed",
                                description: "Please try again later.",
                                variant: "destructive",
                              });
                            } finally {
                              // 清除loading状态
                              setPreviewingFiles(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(itemId);
                                return newSet;
                              });
                            }
                          }
                        }}
                      >
                        {previewingFiles.has(fileChild.id) ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                        <Eye className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            
             
               </div>
        ) : hasFolders ? null : (pagination && pagination.loading) ? null : (
          <div className="text-gray-400 text-center py-8 w-full">No files available</div>
        )}
        
        {/* 固定底部状态栏 - 文件计数和分页 */}
        <div className="fixed bottom-0 left-0 right-0 bg-white z-50" style={{ marginTop: '1px' }}>
          <div className="flex items-center justify-between px-4 py-2">
            {/* 左下角 - 文件计数 */}
            <div className="flex items-center gap-2">
             {pagination && pagination.total > 0 && (
                <span className="text-sm text-gray-700">
                  {(() => {
                    const itemsPerPage = 100;
                    const startItem = (pagination.page - 1) * itemsPerPage + 1;
                    const endItem = Math.min(pagination.page * itemsPerPage, pagination.total);
                    
                    // 如果总文件数不足100个，只显示总数
                    if (pagination.total <= itemsPerPage) {
                      return `Showing ${pagination.total} results`;
                    }
                    
                    // 如果超过100个，显示范围
                    return `Showing ${startItem}-${endItem} of ${pagination.total} results`;
                  })()}
                </span>
              )}
          </div>
            
            {/* 右下角 - 分页UI */}
            <div className="flex items-center gap-4">
              {/* 分页控件 */}
              {pagination && pagination.total > 0 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={Math.ceil(pagination.total / 100)}
                  totalItems={pagination.total}
                  itemsPerPage={100}
                  onPageChange={onPageChange || (() => {})}
                  className="border-t-0"
                  loading={pagination.loading}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 递归收集所有文件夹及其子文件夹下所有文件id
function collectAllFiles(folderId: string, folderChildren: { [id: string]: any[] }): string[] {
  const children: any[] = folderChildren[folderId] || [];
  let fileIds: string[] = [];
  for (const child of children) {
    if ((child as any).isFolder) {
      fileIds = fileIds.concat(collectAllFiles(child.id, folderChildren));
    } else {
      fileIds.push(child.id);
    }
  }
  return fileIds;
}

// 递归收集所有文件夹及其子文件夹下所有文件对象
function collectAllFilesWithObjects(folderId: string, folderChildren: { [id: string]: any[] }, currentPath: string = ''): any[] {
  const children: any[] = folderChildren[folderId] || [];
  let fileObjects: any[] = [];
  for (const child of children) {
    if ((child as any).isFolder) {
      fileObjects = fileObjects.concat(collectAllFilesWithObjects(child.id, folderChildren, currentPath ? `${currentPath}/${child.name}` : child.name));
    } else {
      fileObjects.push({
        ...child,
        folderPath: currentPath || 'Root',
        fullPath: currentPath ? `${currentPath}/${child.name}` : child.name
      });
    }
  }
  return fileObjects;
}

// 递归收集文件夹内的所有文件，包括子文件夹中的文件
async function collectAllFilesRecursively(folderId: string, folderChildren: { [id: string]: any[] }, currentPath: string = '', projectId?: string): Promise<any[]> {
  const children: any[] = folderChildren[folderId] || [];
  let allFiles: any[] = [];
  
  for (const child of children) {
    if ((child as any).isFolder) {
      // 如果是文件夹，需要先加载其内容，然后递归收集
      console.log('🔍 DEBUG: Found subfolder:', child.name, 'loading contents...');
      
      try {
        // 加载子文件夹内容
        const response = await apiRequest<any>(`/api/acc/folders?projectId=${projectId}&folderId=${child.id}`);
        
        let subChildren: any[];
        if (response && response.items && Array.isArray(response.items)) {
          subChildren = response.items;
        } else if (Array.isArray(response)) {
          subChildren = response;
        } else {
          subChildren = [];
        }
        
        console.log('🔍 DEBUG: Subfolder content loaded:', subChildren);
        
        // 递归收集子文件夹中的文件
        const subFolderFiles = await collectAllFilesRecursively(
          child.id, 
          { ...folderChildren, [child.id]: subChildren }, 
          currentPath ? `${currentPath}/${child.name}` : child.name,
          projectId
        );
        
        allFiles.push(...subFolderFiles);
        
      } catch (error) {
        console.error('🔍 DEBUG: Failed to load subfolder content:', child.name, error);
        // 继续处理其他文件，不中断整个流程
      }
    } else {
      // 如果是文件，直接添加
      allFiles.push({
        ...child,
        folderPath: currentPath || 'Root',
        fullPath: currentPath ? `${currentPath}/${child.name}` : child.name
      });
    }
  }
  
  return allFiles;
}

export default function FolderStructure() {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [folderChildren, setFolderChildren] = useState<{ [id: string]: any[] }>({});
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const { selectedProject } = useAppStore();
  const { user } = useAuth();
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('root');
  const [fileApprovals, setFileApprovals] = useState<{ [fileId: string]: string }>({});

  const [downloading, setDownloading] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set()); // 新增：跟踪每个文件的下载状态
  const [previewingFiles, setPreviewingFiles] = useState<Set<string>>(new Set()); // 新增：跟踪每个文件的预览状态
  const [downloadAbortController, setDownloadAbortController] = useState<AbortController | null>(null);
  const [showAddToPackagesDialog, setShowAddToPackagesDialog] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, batch: 0, totalBatches: 0 }); // 新增：下载进度状态
  const [useBackgroundProcessing, setUseBackgroundProcessing] = useState(false); // 新增：是否使用后台处理

  
  // 分页状态
  const [folderPagination, setFolderPagination] = useState<{ [folderId: string]: { page: number; total: number; hasMore: boolean; loading: boolean } }>({});
  
  // 新增：筛选器相关状态（完全自包含）
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('all');
  const [selectedFileType, setSelectedFileType] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [searchScope, setSearchScope] = useState<'current' | 'global'>('current'); // 新增：搜索范围
  const [isLoadingSubfolders, setIsLoadingSubfolders] = useState(false); // 新增：子文件夹加载状态
    const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);
  const [availableFileTypes, setAvailableFileTypes] = useState<string[]>([]);
  
  // 拖拽相关状态
  const [colWidths, setColWidths] = useState<number[]>(DEFAULT_COL_WIDTHS);
  const draggingCol = useRef<number | null>(null);
  const startX = useRef<number>(0);
  const startWidths = useRef<number[]>([]);
  
  const { toast } = useToast();

  // 处理文件选择，支持Shift+点击多选
  const handleItemSelect = (id: string, checked: boolean, event?: React.MouseEvent, itemIndex?: number) => {
    const newSelected = new Set(selectedItems);
    
    // 检查是否按下了Shift键
    if (event?.shiftKey && lastSelectedIndex !== -1 && itemIndex !== undefined) {
      // Shift+点击：选择从lastSelectedIndex到当前itemIndex之间的所有项目
      const currentFolderItems = folderChildren[selectedFolderId] || [];
      const startIndex = Math.min(lastSelectedIndex, itemIndex);
      const endIndex = Math.max(lastSelectedIndex, itemIndex);
      
      // 选择范围内的所有项目
      for (let i = startIndex; i <= endIndex; i++) {
        if (currentFolderItems[i]) {
          newSelected.add(currentFolderItems[i].id);
        }
      }
      
      // 更新lastSelectedIndex为当前点击的项目
      setLastSelectedIndex(itemIndex);
    } else {
      // 普通点击：只选择/取消选择当前项目
      if (checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      
      // 更新lastSelectedIndex为当前点击的项目
      if (itemIndex !== undefined) {
        setLastSelectedIndex(itemIndex);
      }
    }
    
    setSelectedItems(newSelected);
    
    // 更新selectAll状态
    const currentFolderItems = folderChildren[selectedFolderId] || [];
    const allIds = currentFolderItems.map(item => item.id);
    setSelectAll(newSelected.size === allIds.length && allIds.length > 0);
  };

  const { data: topFoldersResponse, isLoading: foldersLoading } = useQuery<{ items: FolderOrFile[]; total: number; hasMore: boolean; page: number; limit: number } | null>({
    queryKey: ['folder-structure', selectedProject?.hubId, selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject?.id || !selectedProject.hubId) return null;
      // 对于 Project API，需要移除 'b.' 前缀
      const projectIdForProjectAPI = selectedProject.id.replace(/^b\./, '');
      
      return await apiRequest<{ items: FolderOrFile[]; total: number; hasMore: boolean; page: number; limit: number }>(`/api/autodesk/hubs/${selectedProject.hubId}/projects/${projectIdForProjectAPI}/folders`);
    },
    enabled: !!selectedProject?.id && !!selectedProject.hubId,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes like PhotoSphere360
  });

  // 提取 topFolders 数组，保持向后兼容
  const topFolders = topFoldersResponse?.items || null;

  const { data: companiesData } = useQuery<CompanyResponse>({
    queryKey: ['companies', 'all', 1],
    queryFn: () => getCompanies({ search: '', page: 1 }),
  });
  const companies = companiesData?.items || [];

  useEffect(() => {
    if (topFoldersResponse && topFoldersResponse.items && topFoldersResponse.items.length > 0) {
      setFolderChildren({ root: topFoldersResponse.items });
      
      // 为 root 文件夹设置初始分页状态
      setFolderPagination(prev => ({
        ...prev,
        root: {
          page: topFoldersResponse.page || 1,
          total: topFoldersResponse.total || topFoldersResponse.items.length,
          hasMore: topFoldersResponse.hasMore || false,
          loading: false
        }
      }));
      
      // 设置根文件夹为展开状态，这样用户可以看到顶级文件夹
      setExpandedFolders(prev => new Set(prev).add('root'));
      
      // 设置根文件夹为初始选择状态，这样用户可以看到顶级文件夹的内容
      setSelectedFolderId('root');
    }
  }, [topFoldersResponse]);

  // 监听来自 Documents.tsx 的 Filter Files 按钮点击事件
  useEffect(() => {
    const handleToggleFilterPanel = () => {
      setShowFilterPanel(prev => !prev);
    };

    window.addEventListener('toggleFilterPanel', handleToggleFilterPanel);
    
    return () => {
      window.removeEventListener('toggleFilterPanel', handleToggleFilterPanel);
    };
  }, []);

  // 新增：筛选逻辑 - 使用防抖技术避免过度重新渲染
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false); // 跟踪是否有活动的搜索结果
  
  // 递归加载所有子文件夹数据的函数
  const loadAllSubfoldersRecursively = useCallback(async (folderId: string, maxDepth: number = 3): Promise<void> => {
    if (maxDepth <= 0) {
      return; // 防止无限递归
    }
    
    const children = folderChildren[folderId] || [];
    const foldersToLoad = children.filter(item => item.isFolder && !folderChildren[item.id]);
    if (foldersToLoad.length === 0) {
      // 如果没有新文件夹需要加载，检查已加载的文件夹是否需要递归
      const loadedFolders = children.filter(item => item.isFolder && folderChildren[item.id]);
      for (const folder of loadedFolders) {
        await loadAllSubfoldersRecursively(folder.id, maxDepth - 1);
      }
      return;
    }
    
    // 并行加载所有子文件夹（提高性能）
    const loadPromises = foldersToLoad.map(async (item) => {
      try {
        const response = await apiRequest<any>(`/api/acc/folders?projectId=${selectedProject?.id}&folderId=${item.id}`);
        
        let subChildren: FolderOrFile[];
        if (response && response.items && Array.isArray(response.items)) {
          subChildren = response.items;
        } else if (Array.isArray(response)) {
          subChildren = response;
        } else {
          subChildren = [];
        }
        
        return { folderId: item.id, children: subChildren };
      } catch (error) {
        return { folderId: item.id, children: [] };
      }
    });
    
    // 等待所有加载完成
    const results = await Promise.all(loadPromises);
    
    // 批量更新文件夹数据
    setFolderChildren(prev => {
      const newData = { ...prev };
      results.forEach(result => {
        newData[result.folderId] = result.children;
      });
      return newData;
    });
    
    // 递归加载下一层 - 对所有新加载的文件夹进行递归
    for (const result of results) {
      if (result.children.length > 0) {
        await loadAllSubfoldersRecursively(result.folderId, maxDepth - 1);
      }
    }
  }, [folderChildren, selectedProject?.id]);

  // 手动搜索函数 - 使用后端搜索API
  const handleSearch = useCallback(async () => {
    
    // 修改：只要有搜索词就尝试搜索，不限制搜索范围
    if (localSearchTerm.trim()) {
      try {
        setIsLoadingSubfolders(true);
        
        // 优先使用快速本地搜索（Current Folder模式）
        if (searchScope === 'current') {
          // 获取当前文件夹的内容
          const currentFolderItems = folderChildren[selectedFolderId] || [];
          
          // 快速本地过滤，参考MyHome的搜索逻辑
          const localMatches = currentFolderItems.filter(item => {
            const itemName = (item.name || '').toLowerCase();
            const searchLower = localSearchTerm.toLowerCase();
            return itemName.includes(searchLower);
          });
          
          setFilteredData(localMatches);
          setIsSearchActive(true); // 标记搜索处于活动状态
          setDebouncedSearchTerm(localSearchTerm);
          setIsLoadingSubfolders(false);
          return;
        }
        
        // 深度搜索模式：使用后端API
        try {
          let searchFolderId: string;
          let searchDepth: number;
          
          // Include Subfolders: 深度搜索
          searchFolderId = selectedFolderId === 'root' ? 'root' : selectedFolderId;
          searchDepth = 10; // 深度搜索
          
          // 增加超时时间到60秒，支持深层搜索
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('搜索超时 - 请尝试更具体的搜索词或在更小的文件夹范围内搜索')), 60000);
          });
          
          // 修复：使用正确的项目ID
          let correctProjectId = selectedProject?.id;
          if (correctProjectId === 'b.533cca91-64e3-4f2d-b329-75d89031bc99') {
            correctProjectId = 'b.c53cca91-64e3-4f2d-b329-75d89031bc99';
          }
          
          const searchUrl = `/api/folders/search?projectId=${correctProjectId}&folderId=${searchFolderId}&query=${encodeURIComponent(localSearchTerm)}&searchDepth=${searchDepth}&searchScope=${searchScope}`;
          
          const searchPromise = apiRequest<any>(searchUrl);
          
          const response = await Promise.race([searchPromise, timeoutPromise]);
          
          if (response && Array.isArray(response)) {
            // 无论结果是否为空，都要设置状态
            if (response.length > 0) {
              // 设置搜索结果，确保数据格式正确
              const formattedResults = response.map((item: any) => ({
                ...item,
                // 确保路径信息正确
                folderPath: item.folderPath || item.fullPath?.split('/').slice(0, -1).join('/') || 'Project Files',
                fullPath: item.fullPath || (item.folderPath ? `${item.folderPath}/${item.name}` : item.name)
              }));
              
              setFilteredData(formattedResults);
              setIsSearchActive(true); // 标记搜索处于活动状态
              
              // 立即设置搜索词状态，确保搜索结果能够显示
              setDebouncedSearchTerm(localSearchTerm);
              
              // 更新分页信息以支持搜索结果的分页
              const newPaginationInfo = {
                page: 1,
                total: formattedResults.length,
                hasMore: formattedResults.length > 100, // 如果超过100个，启用分页
                loading: false
              };
              
              setFolderPagination(prev => ({
                ...prev,
                [selectedFolderId]: newPaginationInfo
              }));
            } else {
              // 搜索结果为空，也要设置状态以显示"未找到结果"
              console.log('⚠️ [FRONTEND SEARCH] No results found from backend');
              setFilteredData([]);
              setIsSearchActive(true);
              setDebouncedSearchTerm(localSearchTerm);
              
              // 更新分页信息
              const newPaginationInfo = {
                page: 1,
                total: 0,
                hasMore: false,
                loading: false
              };
              
              setFolderPagination(prev => ({
                ...prev,
                [selectedFolderId]: newPaginationInfo
              }));
            }
            
            setIsLoadingSubfolders(false);
            return;
          }
        } catch (error) {
          console.error('搜索失败:', error);
        }
        
        // 深度搜索模式：执行前端递归搜索
        const rootChildren = folderChildren['root'] || [];
        const createmasterFolder = rootChildren.find(item => 
          item.isFolder && item.name.toLowerCase().includes('createmaster')
        );
        
        if (createmasterFolder) {
          // 强制加载Createmaster的所有子文件夹，深度20层
          await loadAllSubfoldersRecursively(createmasterFolder.id, 20);
          
          // 特别加载B.Fabric Packages和C.Services Packages
          const createmasterChildren = folderChildren[createmasterFolder.id] || [];
          const fabricPackages = createmasterChildren.find(item => 
            item.isFolder && item.name.toLowerCase().includes('fabric')
          );
          const servicesPackages = createmasterChildren.find(item => 
            item.isFolder && item.name.toLowerCase().includes('services')
          );
          
          if (fabricPackages) {
            await loadAllSubfoldersRecursively(fabricPackages.id, 20);
          }
          
          if (servicesPackages) {
            await loadAllSubfoldersRecursively(servicesPackages.id, 20);
          }
        }
        
        // 然后加载其他文件夹
        await loadAllSubfoldersRecursively(selectedFolderId, 12);
        
        // 等待状态更新
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 检查递归加载后的文件数量
        const allFiles = getCurrentFolderFilesRecursively();
        
        // 直接在前端执行搜索匹配，使用更严格的匹配逻辑
        const searchLower = localSearchTerm.toLowerCase();
        
        // 提取重要单词：过滤掉单个字符、"-"、常见停用词，以及太短的片段（少于3个字符）
        const searchWords = searchLower
          .split(/[\s\-_.,;:()\[\]{}]+/)
          .filter(word => word.length >= 3 && !['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'].includes(word));
        
        // 如果没有有效单词，使用原始搜索词（至少3个字符）
        const validSearchWords = searchWords.length > 0 ? searchWords : (searchLower.trim().length >= 3 ? [searchLower.trim()] : []);
        
        // 更严格的匹配要求：
        // - 如果有多个重要单词，至少匹配70%或至少2个单词
        // - 如果只有1-2个单词，必须全部匹配
        const minWordsToMatch = validSearchWords.length > 2 
          ? Math.max(2, Math.ceil(validSearchWords.length * 0.7)) 
          : validSearchWords.length;
        
        const matchedFiles = allFiles.filter(file => {
          const fileNameLower = file.name.toLowerCase();
          
          // 优先检查完整短语匹配（最高优先级）
          const completeMatch = fileNameLower.includes(searchLower);
          if (completeMatch) {
            // 完整匹配，通过
            return true;
          }
          
          // 如果没有完整匹配，检查单词匹配
          if (validSearchWords.length === 0) {
            return false; // 没有有效搜索词，不匹配
          }
          
          const fileWords = fileNameLower.split(/[\s\-_.]+/).filter(w => w.length >= 3);
          
          // 计算精确匹配的单词数量
          const nameWordMatches = validSearchWords.filter(searchWord => {
            if (fileNameLower.includes(searchWord)) {
              const isFullWord = fileWords.some(fileWord => 
                fileWord === searchWord || 
                fileWord.startsWith(searchWord) || 
                fileWord.endsWith(searchWord)
              );
              return isFullWord || searchWord.length >= 5;
            }
            return false;
          }).length;
          
          // 路径匹配
          let pathMatch = false;
          let pathWordMatches = 0;
          if (file.fullPath) {
            const pathLower = file.fullPath.toLowerCase();
            pathMatch = pathLower.includes(searchLower);
            pathWordMatches = validSearchWords.filter(searchWord => 
              pathLower.includes(searchWord)
            ).length;
          }
          
          // 文件夹路径匹配
          let folderMatch = false;
          if (file.folderPath) {
            const folderPathLower = file.folderPath.toLowerCase();
            folderMatch = folderPathLower.includes(searchLower);
          }
          
          // 匹配条件：完整匹配 或 匹配足够的单词数量
          return pathMatch || folderMatch || nameWordMatches >= minWordsToMatch || pathWordMatches >= minWordsToMatch;
        });
        
        // 如果找到匹配的文件，直接设置为搜索结果
        if (matchedFiles.length > 0) {
          setFilteredData(matchedFiles);
          setIsSearchActive(true); // 标记搜索处于活动状态
          // 立即设置搜索词状态，确保搜索结果能够显示
          setDebouncedSearchTerm(localSearchTerm);
          setIsLoadingSubfolders(false);
          return;
        }
        
        
        // 如果没有匹配，但有文件，显示提示信息
        if (allFiles.length > 0) {
          setFilteredData([]);
          setIsSearchActive(true); // 即使没有结果，也标记搜索已完成
          setIsLoadingSubfolders(false);
          return;
        }
        
        // 继续原有的搜索逻辑
        setDebouncedSearchTerm(localSearchTerm);
        return;
      } catch (error) {
        console.error('搜索过程中出现错误:', error);
        setDebouncedSearchTerm(localSearchTerm);
      } finally {
        setIsLoadingSubfolders(false);
      }
    } else {
      // 本地搜索（当前文件夹或后端搜索失败时的降级方案）
      setDebouncedSearchTerm(localSearchTerm);
    }
  }, [localSearchTerm, searchScope, selectedFolderId, selectedProject?.id, folderChildren]);
  
  // 移除自动搜索，改为手动触发
  // useEffect(() => {
  //   setDebouncedSearchTerm(localSearchTerm);
  // }, [localSearchTerm]);
  
  // 递归收集当前文件夹下所有子文件夹的文件
  const getCurrentFolderFilesRecursively = useCallback(() => {
    const allFiles: any[] = [];
    
    const collectFiles = (folderId: string, folderPath: string = '') => {
      const children = folderChildren[folderId] || [];
      
      children.forEach(item => {
        if (item.isFolder) {
          // 如果是文件夹，递归收集其子文件
          const subFolderPath = folderPath ? `${folderPath}/${item.name}` : item.name;
          collectFiles(item.id, subFolderPath);
        } else {
          // 如果是文件，添加到结果中，并记录其路径
          const fileWithPath = {
            ...item,
            folderPath: folderPath || (selectedFolderId === 'root' ? 'Root' : 'Current Folder'),
            fullPath: folderPath ? `${folderPath}/${item.name}` : item.name
          };
          allFiles.push(fileWithPath);
        }
      });
    };
    
    // 从当前选中的文件夹开始收集
    collectFiles(selectedFolderId);
    return allFiles;
  }, [folderChildren, selectedFolderId]);
  
  // 初始化和筛选逻辑
  useEffect(() => {
    console.log('🔍 [FILTER] useEffect triggered for filtering');
    console.log('🔍 [FILTER] selectedVersion:', selectedVersion);
    console.log('🔍 [FILTER] selectedFileType:', selectedFileType);
    console.log('🔍 [FILTER] dateRange:', dateRange);
    console.log('🔍 [FILTER] searchScope:', searchScope);
    console.log('🔍 [FILTER] debouncedSearchTerm:', debouncedSearchTerm);
    console.log('🔍 [FILTER] isSearchActive:', isSearchActive);
    
    // 如果没有搜索词，重置搜索状态
    if (!debouncedSearchTerm) {
      setIsSearchActive(false);
    }
    
    // 如果有活动的搜索结果，且搜索词仍然存在，不要覆盖搜索结果
    if (isSearchActive && debouncedSearchTerm && filteredData.length > 0) {
      console.log('🔍 [FILTER] Search is active, preserving filteredData');
      return;
    }
    
    // 根据搜索范围选择数据源
    // 在 global 搜索模式下，如果有搜索词，应该使用搜索结果（filteredData），但这里我们需要避免循环依赖
    // 所以如果是在 global 搜索模式下且有搜索词，但 filteredData 为空，则使用递归获取的数据
    const sourceData = searchScope === 'global' && debouncedSearchTerm
      ? (filteredData.length > 0 ? filteredData : getCurrentFolderFilesRecursively())
      : folderChildren[selectedFolderId] || [];
    
    console.log('🔍 [FILTER] sourceData length:', sourceData.length);
    
    // 如果没有筛选条件，设置原始数据
    // 在 Include Subfolders 模式下，只考虑搜索词，不考虑其他筛选器
    const hasActiveFilters = searchScope === 'current' 
      ? (selectedVersion !== 'all' || selectedFileType !== 'all' || dateRange?.from || dateRange?.to)
      : false;
    
    console.log('🔍 [FILTER] hasActiveFilters:', hasActiveFilters);
    
    // 如果没有搜索词且没有筛选条件，设置原始数据
    if (!debouncedSearchTerm && !hasActiveFilters) {
      setFilteredData(sourceData);
      return;
    }
    
    // 执行筛选逻辑
    console.log('🔍 [FILTER] Starting filter with', sourceData.length, 'items');
    const filtered = sourceData.filter(item => {
      // 搜索词筛选 - 使用更严格的匹配逻辑
      if (debouncedSearchTerm) {
        const searchLower = debouncedSearchTerm.toLowerCase();
        
        // 提取重要单词：过滤掉单个字符、"-"、常见停用词
        const searchWords = searchLower
          .split(/[\s\-_.,;:()\[\]{}]+/)
          .filter(word => word.length >= 2 && !['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'].includes(word));
        
        const validSearchWords = searchWords.length > 0 ? searchWords : [searchLower.trim()];
        const minWordsToMatch = Math.max(1, Math.ceil(validSearchWords.length * 0.5)); // 至少匹配50%的单词
        
        const itemNameLower = item.name.toLowerCase();
        
        // 优先检查完整短语匹配（最高优先级）
        const completeMatch = itemNameLower.includes(searchLower);
        if (completeMatch) {
          // 完整匹配，通过
        } else {
          // 如果没有完整匹配，检查单词匹配
          if (validSearchWords.length === 0) {
            return false; // 没有有效搜索词，不匹配
          }
          
          const itemWords = itemNameLower.split(/[\s\-_.]+/).filter(w => w.length >= 3);
          
          // 计算精确匹配的单词数量
          const nameWordMatches = validSearchWords.filter(searchWord => {
            if (itemNameLower.includes(searchWord)) {
              const isFullWord = itemWords.some(itemWord => 
                itemWord === searchWord || 
                itemWord.startsWith(searchWord) || 
                itemWord.endsWith(searchWord)
              );
              return isFullWord || searchWord.length >= 5;
            }
            return false;
          }).length;
          
          // 路径匹配（仅在全局搜索时）
          let pathMatch = false;
          let pathWordMatches = 0;
          if (searchScope === 'global' && item.fullPath) {
            const pathLower = item.fullPath.toLowerCase();
            pathMatch = pathLower.includes(searchLower);
            pathWordMatches = validSearchWords.filter(searchWord => 
              pathLower.includes(searchWord)
            ).length;
          }
          
          // 匹配条件：必须匹配足够的单词数量
          const wordMatch = nameWordMatches >= minWordsToMatch || pathWordMatches >= minWordsToMatch;
          if (!wordMatch) {
            return false; // 单词匹配不足，不匹配
          }
        }
      }
      
      // 仅在 current folder 模式下应用文件特定的筛选
      if (searchScope === 'current') {
        // 如果是文件夹，且应用了文件特定的筛选条件，应该过滤掉
        if (item.isFolder && (selectedVersion !== 'all' || selectedFileType !== 'all' || (dateRange?.from || dateRange?.to))) {
          return false;
        }
        
        // 版本筛选（仅对文件应用）
        if (selectedVersion !== 'all' && !item.isFolder) {
          // 使用与显示逻辑一致的版本提取方法
          let itemVersion = '';
          if (item.versionNumber) {
            const versionStr = item.versionNumber.toString();
            itemVersion = versionStr.startsWith('V') ? versionStr : `V${versionStr}`;
          } else if (item.versionId) {
            const versionMatch = item.versionId.match(/v?(\d+)/i);
            const versionNumber = versionMatch ? versionMatch[1] : '1';
            itemVersion = `V${versionNumber}`;
          } else {
            itemVersion = 'V1';
          }
          
          if (itemVersion !== selectedVersion) {
            return false;
          }
        }
        
        // 文件类型筛选（仅对文件应用）
        if (selectedFileType !== 'all' && !item.isFolder) {
          const itemType = item.fileType || item.type || item.name.split('.').pop()?.toLowerCase();
          if (!itemType || itemType !== selectedFileType) {
            return false;
          }
        }
        
        // 日期范围筛选（仅对文件应用）
        if ((dateRange?.from || dateRange?.to) && !item.isFolder) {
          const itemDate = item.lastModifiedTime || item.versionCreateTime;
          if (itemDate) {
            const itemDateObj = new Date(itemDate);
            if (dateRange.from && itemDateObj < dateRange.from) {
              return false;
            }
            if (dateRange.to && itemDateObj > dateRange.to) {
              return false;
            }
          }
        }
      }
      
      // 如果通过了所有筛选条件，返回 true
      return true;
    });
    
    console.log('🔍 [FILTER] Filtered result:', filtered.length, 'items');
    console.log('🔍 [FILTER] Setting filteredData');
    setFilteredData(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderChildren, selectedFolderId, debouncedSearchTerm, selectedVersion, selectedFileType, dateRange, searchScope, getCurrentFolderFilesRecursively, isSearchActive]);

  // 新增：更新筛选器选项的 useEffect
  useEffect(() => {
    const currentData = folderChildren[selectedFolderId] || [];
    if (currentData.length > 0) {
      const versions = extractVersionsFromFiles(currentData.filter(item => !item.isFolder));
      const fileTypes = extractFileTypesFromFiles(currentData.filter(item => !item.isFolder));
      
      setAvailableVersions(versions);
      setAvailableFileTypes(fileTypes);
    }
  }, [folderChildren, selectedFolderId]);

  const handleToggle = async (folder: Folder) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folder.id)) {
        next.delete(folder.id);
      } else {
        next.add(folder.id);
      }
      return next;
    });
    
    // 特殊处理根文件夹
    if (folder.id === 'root') {
      // 确保根文件夹的数据已经加载
      if (topFoldersResponse && topFoldersResponse.items && topFoldersResponse.items.length > 0) {
        setFolderChildren(prev => ({ ...prev, root: topFoldersResponse.items }));
        setSelectedFolderId('root');
      }
      return;
    }
    
    // Also select the folder to show its contents in the file list
    if (!folderChildren[folder.id]) {
      setSelectedFolderId(folder.id); // 立即更新selectedFolderId以触发loading UI
      setLoadingFolders(prev => new Set(prev).add(folder.id));
      try {
        const response = await apiRequest<any>(`/api/acc/folders?projectId=${selectedProject?.id}&folderId=${folder.id}`);
        
        // 处理分页响应
        let children: FolderOrFile[];
        if (response && response.items && Array.isArray(response.items)) {
          // 新的分页响应格式
          children = response.items;
          // 设置分页状态
          setFolderPagination(prev => ({
            ...prev,
            [folder.id]: {
              page: response.page || 1,
              total: response.total || children.length,
              hasMore: response.hasMore || false,
              loading: false
            }
          }));
        } else if (Array.isArray(response)) {
          // 旧的数组响应格式（向后兼容）
          children = response;
          // 设置分页状态（数组响应不支持分页）
          setFolderPagination(prev => ({
            ...prev,
            [folder.id]: {
              page: 1,
              total: children.length,
              hasMore: false,
              loading: false
            }
          }));
        } else {
          console.warn(`Unexpected response format:`, response);
          children = [];
          
          // 设置分页状态
          setFolderPagination(prev => ({
            ...prev,
            [folder.id]: {
              page: 1,
              total: 0,
              hasMore: false,
              loading: false
            }
          }));
        }
        
        setFolderChildren(prev => ({ ...prev, [folder.id]: children }));
      } catch (error) {
        console.error(`Failed to load folder contents for ${folder.name}:`, error);
        // Fallback: Set empty array instead of showing error
        setFolderChildren(prev => ({ ...prev, [folder.id]: [] }));
      } finally {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(folder.id);
          return next;
        });
      }
    } else {
      setSelectedFolderId(folder.id);
    }
  };

  // 加载更多文件（分页加载）
  const loadMoreFiles = async (folderId: string) => {
    // 根文件夹不需要分页加载
    if (folderId === 'root') {
      return;
    }
    
    const pagination = folderPagination[folderId];
    if (!pagination || !pagination.hasMore || pagination.loading) {
      return;
    }
    
    
    // 设置加载状态
    setFolderPagination(prev => ({
      ...prev,
      [folderId]: { ...prev[folderId], loading: true }
    }));
    
    try {
      const nextPage = pagination.page + 1;
      const response = await apiRequest<any>(`/api/acc/folders/${folderId}/load-more?projectId=${selectedProject?.id}&page=${nextPage}&limit=200`);
      
      if (response && response.items && Array.isArray(response.items)) {
        
        // 将新文件追加到现有文件列表
        setFolderChildren(prev => ({
          ...prev,
          [folderId]: [...(prev[folderId] || []), ...response.items]
        }));
        
        // 更新分页状态
        setFolderPagination(prev => ({
          ...prev,
          [folderId]: {
            page: nextPage,
            total: response.total || pagination.total,
            hasMore: response.hasMore || false,
            loading: false
          }
        }));
      }
    } catch (error) {
      console.error(`[Load More] Failed to load more files:`, error);
      toast({
        title: "Load failed",
        description: "Unable to load more files, please try again later",
        variant: "destructive",
      });
      
      // 重置加载状态
      setFolderPagination(prev => ({
        ...prev,
        [folderId]: { ...prev[folderId], loading: false }
      }));
    }
  };

  const handleFolderTreeClick = async (folder: Folder) => {
    // 特殊处理根文件夹
    if (folder.id === 'root') {
      // 确保根文件夹的数据已经加载
      if (topFoldersResponse && topFoldersResponse.items && topFoldersResponse.items.length > 0) {
        setFolderChildren(prev => ({ ...prev, root: topFoldersResponse.items }));
        setSelectedFolderId('root');
      }
      return;
    }
    
    if (!folderChildren[folder.id]) {
      // API调用参数
      setSelectedFolderId(folder.id); // 立即更新selectedFolderId以触发loading UI
      setLoadingFolders(prev => new Set(prev).add(folder.id));
      try {
        const response = await apiRequest<any>(`/api/acc/folders?projectId=${selectedProject?.id}&folderId=${folder.id}`);
        
        // 处理分页响应
        let children: FolderOrFile[];
        if (response && response.items && Array.isArray(response.items)) {
          // 新的分页响应格式
          children = response.items;
          // 设置分页状态
          setFolderPagination(prev => {
            const newState = {
              ...prev,
              [folder.id]: {
                page: response.page || 1,
                total: response.total || children.length,
                hasMore: response.hasMore || false,
                loading: false
              }
            };
            return newState;
          });
        } else if (Array.isArray(response)) {
          // 旧的数组响应格式（向后兼容）
          children = response;
          // 设置分页状态（数组响应不支持分页）
          setFolderPagination(prev => ({
            ...prev,
            [folder.id]: {
              page: 1,
              total: children.length,
              hasMore: false,
              loading: false
            }
          }));
        } else {
          console.warn(`Unexpected response format:`, response);
          children = [];
          
          // 设置分页状态
          setFolderPagination(prev => ({
            ...prev,
            [folder.id]: {
              page: 1,
              total: 0,
              hasMore: false,
              loading: false
            }
          }));
        }
        
        setFolderChildren(prev => ({ ...prev, [folder.id]: children }));
      } catch (error) {
        console.error(`Failed to load folder contents for tree click ${folder.name}:`, error);
        toast({
          title: "Failed to load folder",
          description: `Could not load contents for ${folder.name}`,
          variant: "destructive",
        });
      } finally {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(folder.id);
          return next;
        });
      }
    } else {
      setSelectedFolderId(folder.id);
    }
  };

  // 拖拽处理函数
  const handleMouseDown = (colIdx: number, e: React.MouseEvent) => {
    draggingCol.current = colIdx;
    startX.current = e.clientX;
    startWidths.current = [...colWidths];
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (draggingCol.current === null) return;
    const delta = e.clientX - startX.current;
    const idx = draggingCol.current;
    const newWidths = [...startWidths.current];
    newWidths[idx] = Math.max(40, startWidths.current[idx] + delta);
    setColWidths(newWidths);
  };
  
  const handleMouseUp = () => {
    draggingCol.current = null;
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handlePreviewFolderClick = async (folder: Folder) => {
    // 特殊处理根文件夹
    if (folder.id === 'root') {
      // 确保根文件夹的数据已经加载
      if (topFoldersResponse && topFoldersResponse.items && topFoldersResponse.items.length > 0) {
        setFolderChildren(prev => ({ ...prev, root: topFoldersResponse.items }));
        setSelectedFolderId('root');
      }
      return;
    }
    
    // 清除搜索状态，以便显示文件夹内容而不是搜索结果
    setIsSearchActive(false);
    setDebouncedSearchTerm('');
    setFilteredData([]);
    
    if (!folderChildren[folder.id]) {
      // Preview API调用参数
      setSelectedFolderId(folder.id); // 立即更新selectedFolderId以触发loading UI
      setLoadingFolders(prev => new Set(prev).add(folder.id));
      try {
        const response = await apiRequest<any>(`/api/acc/folders?projectId=${selectedProject?.id}&folderId=${folder.id}`);
        
        // 处理分页响应
        let children: FolderOrFile[];
        if (response && response.items && Array.isArray(response.items)) {
          // 新的分页响应格式
          children = response.items;
        } else if (Array.isArray(response)) {
          // 旧的数组响应格式（向后兼容）
          children = response;
        } else {
          console.warn(`Unexpected response format:`, response);
          children = [];
        }
        
        setFolderChildren(prev => ({ ...prev, [folder.id]: children }));
      } catch {
        // Optional: error handling
      } finally {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(folder.id);
          return next;
        });
      }
    } else {
      setSelectedFolderId(folder.id);
    }
  };

  // 处理页面切换
  const handlePageChange = async (page: number) => {
    // 检查是否是搜索结果的分页（有搜索词时就是搜索结果分页）
    const isSearchResultPagination = debouncedSearchTerm;
    
    // 如果是搜索结果的分页，直接更新页码（不需要API调用）
    if (isSearchResultPagination) {
      console.log(`📄 搜索结果分页: 切换到第${page}页`);
      setFolderPagination(prev => ({
        ...prev,
        [selectedFolderId]: {
          ...prev[selectedFolderId],
          page: page,
          loading: false
        }
      }));
      return;
    }
    
    if (!selectedFolderId || selectedFolderId === 'root') {
      return;
    }
    
    const pagination = folderPagination[selectedFolderId];
    if (!pagination || page === pagination.page) {
      return;
    }
    
    
    // 立即更新分页状态，提供即时UI反馈
    setFolderPagination(prev => ({
      ...prev,
      [selectedFolderId]: { 
        ...prev[selectedFolderId], 
        page: page,
        loading: true 
      }
    }));
    
    // 记录开始时间，确保最小loading时间
    const startTime = Date.now();
    const minLoadingTime = 500; // 最小loading时间500ms
    
    try {
      const response = await apiRequest<any>(`/api/acc/folders/${selectedFolderId}/load-more?projectId=${selectedProject?.id}&page=${page}&limit=100`);
      
      if (response && response.items && Array.isArray(response.items)) {
        // 累积数据：将新页面的数据添加到现有数据中
        setFolderChildren(prev => {
          const existingItems = prev[selectedFolderId] || [];
          const newItems = response.items;
          
          // 检查是否已经包含这个页面的数据（避免重复）
          const startIndex = (page - 1) * 100;
          const endIndex = startIndex + 100;
          
          // 如果现有数据长度足够，说明这个页面已经加载过
          if (existingItems.length >= endIndex) {
            return prev;
          }
          
          // 创建新的累积数组
          const accumulatedItems = [...existingItems];
          
          // 填充缺失的数据（用null占位）
          while (accumulatedItems.length < startIndex) {
            accumulatedItems.push(null);
          }
          
          // 添加新页面的数据
          for (let i = 0; i < newItems.length; i++) {
            accumulatedItems[startIndex + i] = newItems[i];
          }
          
          return {
            ...prev,
            [selectedFolderId]: accumulatedItems
          };
        });
        
        // 确保最小loading时间
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          // 更新分页状态（设置loading为false）
          setFolderPagination(prev => ({
            ...prev,
            [selectedFolderId]: {
              ...prev[selectedFolderId],
              total: response.total || pagination.total,
              hasMore: response.hasMore || false,
              loading: false
            }
          }));
        }, remainingTime);
      }
    } catch (error) {
      console.error(`[Page Change] Failed to load page ${page}:`, error);
      toast({
        title: "Page load failed",
        description: "Unable to load the requested page, please try again later",
        variant: "destructive",
      });
      
      // 确保最小loading时间
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      setTimeout(() => {
        // 重置加载状态
        setFolderPagination(prev => ({
          ...prev,
          [selectedFolderId]: { ...prev[selectedFolderId], loading: false }
        }));
      }, remainingTime);
    }
  };

  // Handle select all functionality
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      // Select all items in current folder
      const currentFolderItems = folderChildren[selectedFolderId] || [];
      const allIds = currentFolderItems.map(item => item.id);
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  // Update selectAll state based on selectedItems
  useEffect(() => {
    const currentFolderItems = folderChildren[selectedFolderId] || [];
    const allIds = currentFolderItems.map(item => item.id);
    const isAllSelected = allIds.length > 0 && allIds.every(id => selectedItems.has(id));
    setSelectAll(isAllSelected);
  }, [selectedItems, selectedFolderId, folderChildren]);

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+A 全选
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault();
        const currentFolderItems = folderChildren[selectedFolderId] || [];
        const allIds = currentFolderItems.map(item => item.id);
        setSelectedItems(new Set(allIds));
        setSelectAll(true);
      }
      // Escape 取消选择
      if (event.key === 'Escape') {
        setSelectedItems(new Set());
        setSelectAll(false);
        setLastSelectedIndex(-1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedFolderId, folderChildren]);

  // Batch download with chunking for large files and Reports system
  const handleBatchDownload = async () => {
    // 立即显示分析进度提示和进度条UI
    toast({
      title: "🔍 Analyzing Files",
      description: `Analyzing selected items to determine download method...`,
      duration: 5000,
    });

    // 立即显示进度条UI
    setDownloading(true);
    setDownloadProgress({ current: 0, total: 0, batch: 1, totalBatches: 1 });

    // 添加调试信息
    console.log('🔍 DEBUG: handleBatchDownload called');
    console.log('🔍 DEBUG: selectedFolderId:', selectedFolderId);
    console.log('🔍 DEBUG: selectedItems:', Array.from(selectedItems));
    console.log('🔍 DEBUG: folderChildren keys:', Object.keys(folderChildren));
    console.log('🔍 DEBUG: folderChildren[selectedFolderId]:', folderChildren[selectedFolderId]);
    
    // Collect selected items from current layer, including files from selected folders
    const selectedObjs = (Array.isArray(folderChildren[selectedFolderId]) ? folderChildren[selectedFolderId] : [])
      .filter(child => child && selectedItems.has(child.id));
    
    console.log('🔍 DEBUG: selectedObjs:', selectedObjs);
    
    // 递归收集所有选中的文件和文件夹内的文件
    const allSelectedIds: string[] = [];
    const allSelectedFiles: any[] = [];
    
    const collectSelectedItems = async (items: any[], parentPath: string = '') => {
      for (const item of items) {
        if (selectedItems.has(item.id)) {
          if (item.isFolder) {
            // 如果是文件夹，递归收集其所有子文件
            console.log('🔍 DEBUG: Processing folder:', item.name, item.id);
            console.log('🔍 DEBUG: folderChildren for this folder:', folderChildren[item.id]);
            
            // 检查文件夹是否已加载子内容
            if (!folderChildren[item.id]) {
                      console.log('🔍 DEBUG: Folder not expanded, loading folder content silently:', item.name);
                      // 不展开UI，静默加载文件夹内容
                      // setExpandedFolders(prev => new Set([...prev, item.id])); // 移除UI展开
                      // setSelectedFolderId(item.id); // 移除选择变化
                      setLoadingFolders(prev => new Set(prev).add(item.id));
                      
                      try {
                        // 加载文件夹内容
                        const response = await apiRequest<any>(`/api/acc/folders?projectId=${selectedProject?.id}&folderId=${item.id}`);
                
                let children: any[];
                if (response && response.items && Array.isArray(response.items)) {
                  children = response.items;
                } else if (Array.isArray(response)) {
                  children = response;
                } else {
                  children = [];
                }
                
                // 更新文件夹子内容
                setFolderChildren(prev => ({ ...prev, [item.id]: children }));
                setLoadingFolders(prev => {
                  const next = new Set(prev);
                  next.delete(item.id);
                  return next;
                });
                
                console.log('🔍 DEBUG: Folder content loaded:', children);
                
                // 递归收集文件夹内的所有文件（包括子文件夹中的文件）
                const allFilesInFolder = await collectAllFilesRecursively(item.id, { ...folderChildren, [item.id]: children }, item.name, selectedProject?.id);
                console.log('🔍 DEBUG: allFilesInFolder found after loading:', allFilesInFolder);
                allSelectedIds.push(...allFilesInFolder.map(f => f.id));
                allSelectedFiles.push(...allFilesInFolder);
                
              } catch (error) {
                console.error('🔍 DEBUG: Failed to load folder content:', error);
                alert(`Failed to load folder "${item.name}" contents. Please try again.`);
                continue;
              }
            } else {
              // 文件夹已经展开，递归收集所有文件
              const allFilesInFolder = await collectAllFilesRecursively(item.id, folderChildren, item.name, selectedProject?.id);
              console.log('🔍 DEBUG: allFilesInFolder found (already expanded):', allFilesInFolder);
              allSelectedIds.push(...allFilesInFolder.map(f => f.id));
              allSelectedFiles.push(...allFilesInFolder);
            }
          } else {
            // 如果是文件，直接添加
            allSelectedIds.push(item.id);
            allSelectedFiles.push({
              ...item,
              folderPath: parentPath || 'Root',
              fullPath: parentPath ? `${parentPath}/${item.name}` : item.name
            });
          }
        }
      }
    };
    
    // 为直接选择的文件确定正确的父路径
    // 构建从Project Files开始的完整路径
    const buildFolderPath = (folderId: string): string => {
      if (folderId === 'root') {
        return 'Project Files';
      }
      
      // 查找当前文件夹的名称
      const currentFolder = folderChildren[folderId]?.find(item => item.id === folderId);
      if (currentFolder) {
        return `Project Files/${currentFolder.name}`;
      }
      
      // 如果找不到，尝试从folderChildren中查找
      for (const [id, children] of Object.entries(folderChildren)) {
        const folder = children?.find(item => item.id === folderId);
        if (folder) {
          return `Project Files/${folder.name}`;
        }
      }
      
      return 'Project Files';
    };
    
    const currentFolderPath = buildFolderPath(selectedFolderId);
    console.log('🔍 DEBUG: currentFolderPath:', currentFolderPath);
    
    await collectSelectedItems(selectedObjs, currentFolderPath);
    
    console.log('🔍 DEBUG: allSelectedIds:', allSelectedIds);
    console.log('🔍 DEBUG: allSelectedFiles:', allSelectedFiles);
    
    if (allSelectedIds.length === 0) {
      // 检查是否选择了文件夹但没有文件
      const hasSelectedFolders = selectedObjs.some(item => item.isFolder);
      const hasSelectedFiles = selectedObjs.some(item => !item.isFolder);
      
      if (hasSelectedFolders && !hasSelectedFiles) {
        // 选择了文件夹但没有文件的情况
        toast({
          title: "📁 Empty Folders Selected",
          description: "The selected folders do not contain any files. Please select folders with files or individual files to download.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        // 没有选择任何项目
        toast({
          title: "⚠️ No Items Selected",
          description: "Please select files or folders to download.",
          variant: "destructive",
          duration: 5000,
        });
      }
      
      // 清理状态
      setDownloading(false);
      setDownloadProgress({ current: 0, total: 0, batch: 0, totalBatches: 0 });
      return;
    }
    
    console.log(`📁 Selected ${allSelectedIds.length} files from ${selectedObjs.length} items`);

    // 需要分析所有选中项目（包括文件夹内的文件）来决定是否使用后台处理
    console.log('📊 Analyzing selected items for background processing...');
    // 更新进度条总数（现在我们知道实际文件数量）
    setDownloadProgress({ current: 0, total: allSelectedIds.length, batch: 1, totalBatches: 1 });
    
    let shouldUseBackgroundProcessing = false;
    let analysisData = null;
    
    try {
      // 先发送一个预分析请求给后端，让后端告诉我们总文件数和大小
      const { apiRequest } = await import('@/lib/queryClient');
      const preAnalysisResponse = await apiRequest('/api/analyze-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: selectedProject?.id,
          itemIds: allSelectedIds,
        }),
      });

      // 永久解决方案：apiRequest返回的是解析后的数据，不是Response对象
      if (preAnalysisResponse && preAnalysisResponse.totalFiles !== undefined) {
        analysisData = preAnalysisResponse;
        console.log(`📊 Download analysis: ${analysisData.totalFiles} files, ${(analysisData.totalSize / (1024 * 1024)).toFixed(2)} MB total`);
        console.log(`📊 Analysis details: totalSize=${analysisData.totalSize}, threshold=${50 * 1024 * 1024}, shouldTrigger=${analysisData.totalSize > 50 * 1024 * 1024}`);
        
        // 检查是否触发了早期退出
        if (analysisData.earlyExit && analysisData.thresholdReached) {
          console.log(`🚨 Early exit detected - threshold reached during analysis`);
          shouldUseBackgroundProcessing = true;
        } else {
          shouldUseBackgroundProcessing = analysisData.totalFiles > 10 || analysisData.totalSize > 50 * 1024 * 1024; // 超过10个文件或50MB
        }
        console.log(`🤔 Should use background processing: ${shouldUseBackgroundProcessing} (early exit: ${analysisData.earlyExit})`);
      } else {
        console.error('❌ Pre-analysis failed, using fallback analysis');
        // 如果预分析失败，使用原有的简单分析
        const totalSize = allSelectedFiles.reduce((sum, obj) => sum + (obj.size || 0), 0);
        const totalSizeMB = totalSize / (1024 * 1024);
        shouldUseBackgroundProcessing = allSelectedIds.length > 10 || totalSizeMB > 50; // 超过10个文件或50MB
        console.log(`📊 Fallback analysis: ${allSelectedIds.length} items, ${totalSizeMB.toFixed(2)} MB total`);
        console.log(`🤔 Should use background processing (fallback): ${shouldUseBackgroundProcessing}`);
      }
    } catch (error) {
      console.error('❌ Analysis error:', error);
      // 发生错误时使用原有逻辑
      const totalSize = allSelectedFiles.reduce((sum, obj) => sum + (obj.size || 0), 0);
      const totalSizeMB = totalSize / (1024 * 1024);
      shouldUseBackgroundProcessing = allSelectedIds.length > 0 || totalSizeMB > 0; // 临时改为任何选择都触发，方便测试
      console.log(`📊 Error fallback analysis: ${allSelectedIds.length} items, ${totalSizeMB.toFixed(2)} MB total`);
    }
    
    if (shouldUseBackgroundProcessing) {
      setUseBackgroundProcessing(true);
      console.log('🔄 Switching to background processing mode');
      
      try {
        // 使用后台处理模式 - 创建报告而不是直接下载
        const reportData = {
          accProjectId: selectedProject?.id,
          title: `Your Download is Ready: [${selectedProject?.name || 'Unknown Project'}] - bulk_download_${Date.now()}`,
          type: 'bulk_download',
          createdBy: user?.email || 'user@example.com',
          fileCount: allSelectedIds.length,
          totalSize: allSelectedFiles.reduce((sum, obj) => sum + (obj.size || 0), 0),
          metadata: {
            itemIds: allSelectedIds,
            filePaths: allSelectedFiles.map(f => ({ id: f.id, path: f.fullPath })),
            zipName: `bulk_download_${Date.now()}.zip`
          }
        };
        
        console.log('📊 Creating background report for', reportData.fileCount, 'files');
        
        // 如果文件太多，分批处理
        // 🔧 优化：根据文件大小智能分批，而不是固定数量
        // 策略：每批最多200个文件或500MB（以先达到为准）
        const maxFilesPerBatch = 200; // 增加到200个文件
        const maxBatchSizeMB = 500; // 每批最多500MB
        const totalBatches = Math.ceil(reportData.fileCount / maxFilesPerBatch);
        
        if (totalBatches > 1) {
          console.log(`📦 Large download detected: ${reportData.fileCount} files, splitting into ${totalBatches} batches`);
          
          // 分批创建报告 - 添加延迟避免并发过载
          const batchReports = [];
          for (let i = 0; i < totalBatches; i++) {
            const startIndex = i * maxFilesPerBatch;
            const endIndex = Math.min(startIndex + maxFilesPerBatch, reportData.fileCount);
            const batchItemIds = allSelectedIds.slice(startIndex, endIndex);
            const batchFiles = allSelectedFiles.slice(startIndex, endIndex);
            const batchFilePaths = batchFiles.map(f => ({ id: f.id, path: f.fullPath }));
            
            const batchReportData = {
              accProjectId: selectedProject?.id,
              title: `Your Download is Ready: [${selectedProject?.name || 'Unknown Project'}] - bulk_download [${i + 1}/${totalBatches}]_${Date.now()}`,
              type: 'bulk_download',
              createdBy: user?.email || 'user@example.com',
              fileCount: batchItemIds.length,
              totalSize: batchFiles.reduce((sum, f) => sum + (f.size || 0), 0),
              metadata: {
                itemIds: batchItemIds,
                filePaths: batchFilePaths,
                zipName: `bulk_download_part_${i + 1}_of_${totalBatches}_${Date.now()}.zip`,
                batchNumber: i + 1,
                totalBatches: totalBatches,
                delayStart: i > 0 // 除了第一个批次，其他批次需要等待
              }
            };
            
            console.log(`📦 Creating batch ${i + 1}/${totalBatches} with ${batchItemIds.length} files`);
            
            const batchResponse = await apiRequest('/api/reports', {
              method: 'POST',
              body: JSON.stringify(batchReportData)
            });
            
            if (batchResponse && batchResponse.id) {
              batchReports.push(batchResponse.id);
              console.log(`✅ Batch ${i + 1} report created:`, batchResponse.id);
              
              // 🔧 优化：添加短暂延迟，让后端队列系统有序处理
              // 后端已有队列管理，这里只需要短暂延迟避免同时提交
              if (i < totalBatches - 1) {
                console.log(`⏳ Waiting 5 seconds before creating next batch (backend queue will manage processing)...`);
                await new Promise(resolve => setTimeout(resolve, 5000)); // 5秒延迟
              }
            } else {
              throw new Error(`Failed to create batch ${i + 1} report`);
            }
          }
          
          // 显示多批次进度UI
          setDownloading(true);
          setDownloadProgress({ 
            current: 0, 
            total: reportData.fileCount, 
            batch: 0, 
            totalBatches: totalBatches 
          });
          
          // 显示多批次通知
          setTimeout(() => {
            toast({
              title: "📋 Multi-part Download Processing",
              description: `Your download (${reportData.fileCount} files) is being processed in ${totalBatches} parts. You'll receive emails when each part is ready. Check the Reports tab for progress.`,
              duration: 5000,
            });
          }, 500);
          
          // 清理状态并退出
          setDownloading(false);
          setDownloadAbortController(null);
          setDownloadProgress({ current: 0, total: 0, batch: 0, totalBatches: 0 });
          setSelectedItems(new Set());
          setSelectAll(false);
          setUseBackgroundProcessing(false);
          
          return; // 退出函数
        } else {
          // 单批次处理
          const response = await apiRequest('/api/reports', {
            method: 'POST',
            body: JSON.stringify(reportData)
          });
          
          if (!response || !response.id) {
            throw new Error('Background report request failed');
          }
          
          // 单批次处理成功
          console.log('✅ Background report created:', response.id);
          
          // 显示进度UI
          setDownloading(true);
          setDownloadProgress({ 
            current: 0, 
            total: reportData.fileCount, 
            batch: 0, 
            totalBatches: Math.ceil(reportData.fileCount / 10) 
          });
          
          // 显示成功通知
          toast({
            title: "Background Processing Started",
            description: `Processing ${reportData.fileCount} files in background. You'll be notified when ready.`,
            duration: 5000,
          });
          
          // 清理选择状态
          setSelectedItems(new Set());
          setSelectAll(false);
          setUseBackgroundProcessing(false);
          
          return; // 退出整个函数
        }
        
      } catch (error) {
        console.error('❌ Background processing failed:', error);
        toast({
          title: "Download Failed",
          description: error.message || "Failed to download files",
          variant: "destructive",
          duration: 5000,
        });
        
        // 清理状态
        setDownloading(false);
        setDownloadAbortController(null);
        setDownloadProgress({ current: 0, total: 0, batch: 0, totalBatches: 0 });
        setUseBackgroundProcessing(false);
        return;
      }
    }
    
    // 如果不使用后台处理，继续原有的下载逻辑
    console.log('📦 Batch download not needed, using standard download');

    // 分批下载配置
    const MAX_TOTAL_SIZE_MB = 100; // 每批最大100MB
    const LARGE_FILE_THRESHOLD_MB = 100; // 大文件阈值100MB
    
    // 将文件分批 - 智能分组策略
    const batches = [];
    let currentBatch = [];
    let currentBatchSize = 0;
    
    for (let i = 0; i < allSelectedIds.length; i++) {
      const fileId = allSelectedIds[i];
      const fileObj = allSelectedFiles.find(obj => obj.id === fileId);
      const fileSizeMB = fileObj?.size ? fileObj.size / (1024 * 1024) : 1; // 将字节转换为MB，默认1MB
      
      // 如果单个文件超过100MB，单独成批
      if (fileSizeMB >= LARGE_FILE_THRESHOLD_MB) {
        // 先保存当前批次（如果有的话）
        if (currentBatch.length > 0) {
          batches.push([...currentBatch]);
          currentBatch = [];
          currentBatchSize = 0;
        }
        // 大文件单独成批
        batches.push([fileId]);
        continue;
      }
      
      // 小文件：如果当前批次加上这个文件会超过100MB，先保存当前批次
      if (currentBatchSize + fileSizeMB > MAX_TOTAL_SIZE_MB && currentBatch.length > 0) {
        batches.push([...currentBatch]);
        currentBatch = [];
        currentBatchSize = 0;
      }
      
      // 添加文件到当前批次
      currentBatch.push(fileId);
      currentBatchSize += fileSizeMB;
    }
    
    // 添加最后一个批次
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
    }
    
    // 显示批次分组详情
    batches.forEach((batch, index) => {
      const batchSize = batch.reduce((total, fileId) => {
        const fileObj = selectedObjs.find(obj => obj.id === fileId);
        const fileSizeMB = fileObj?.size ? fileObj.size / (1024 * 1024) : 1;
        return total + fileSizeMB;
      }, 0);
    });
    
    // 创建 AbortController 用于取消下载
    const abortController = new AbortController();
    setDownloadAbortController(abortController);
    setDownloading(true);
    setDownloadProgress({ current: 0, total: allSelectedIds.length, batch: 0, totalBatches: batches.length });
    
    // 设置超时时间 - 根据批次数量调整
    const timeoutMinutes = Math.max(5, batches.length * 2); // 每批次2分钟
    const timeoutMs = timeoutMinutes * 60 * 1000;
    
    // 设置超时定时器
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, timeoutMs);
    
    try {
      // 启动模拟进度更新
      const updateInterval = Math.max(500, Math.min(2000, allSelectedIds.length * 10));
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev.current < prev.total) {
            const increment = Math.max(1, Math.floor(prev.total / 20));
            return { ...prev, current: Math.min(prev.current + increment, prev.total) };
          }
          return prev;
        });
      }, updateInterval);
      
      // 分批下载
      let processedFiles = 0;
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const batchZipName = batches.length > 1 
          ? `batch_download_part_${batchIndex + 1}_of_${batches.length}_${Date.now()}.zip`
          : `batch_download_${Date.now()}.zip`;
        
        const batchType = batch.length === 1 ? 'single file' : `${batch.length} files`;
        // 使用fetch直接处理ZIP响应，避免JSON解析错误
        const response = await fetch('/api/download-multiple', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: abortController.signal,
          body: JSON.stringify({
            projectId: selectedProject?.id,
            itemIds: batch,
            zipName: batchZipName,
            userEmail: user?.email || 'user@example.com',
            filePaths: batch.map(id => {
              const fileObj = allSelectedFiles.find(obj => obj.id === id);
              return { id, path: fileObj?.fullPath || fileObj?.name || 'unknown' };
            })
          }),
        });
        
        if (!response.ok) {
          let msg = 'Batch download failed';
          try {
            const data = await response.json();
            msg = data?.error || data?.message || msg;
          } catch {}
          // Check if not logged in
          if (msg.includes('Please login to Autodesk first')) {
            setShowLoginAlert(true);
            return;
          }
          throw new Error(msg);
        }
        
        // 直接处理ZIP文件下载
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = batchZipName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log(`✅ Batch ${batchIndex + 1} downloaded: ${batchZipName}`);
        
        processedFiles += batch.length;
        // 更新进度
        setDownloadProgress({ 
          current: processedFiles, 
          total: allSelectedIds.length, 
          batch: batchIndex + 1, 
          totalBatches: batches.length 
        });
        
        // 如果不是最后一个批次，稍等一下再处理下一个
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 等待1秒
        }
      }
      
      // 清除进度更新定时器
      clearInterval(progressInterval);
      // 清除超时定时器
      clearTimeout(timeoutId);
      
      // 确保进度显示为100%
      setDownloadProgress(prev => ({ ...prev, current: prev.total }));
      
      // 显示成功提示
      toast({
        title: "Download completed",
        description: `Successfully downloaded ${allSelectedIds.length} files in ${batches.length} batch${batches.length > 1 ? 'es' : ''}`,
      });
      
    } catch (error: any) {
      // 清除超时定时器
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        // 只有在用户主动取消时才显示错误提示
        if (downloadAbortController && !downloadAbortController.signal.aborted) {
          toast({
            title: "Download cancelled",
            description: "Download was cancelled due to timeout or user action",
            variant: "destructive",
          });
        }
        return;
      }
      if (error?.message?.includes('Please login to Autodesk first')) {
        setShowLoginAlert(true);
      } else {
        console.error('[BATCH-DOWNLOAD] Error:', error);
        // 延迟显示错误，给下载一些时间完成
        setTimeout(() => {
          if (downloading) { // 如果还在下载状态，说明真的失败了
            toast({
              title: "Download failed",
              description: error?.message || 'Batch download failed',
              variant: "destructive",
            });
          }
        }, 1000);
      }
    } finally {
      setDownloading(false);
      setDownloadAbortController(null);
      setDownloadProgress({ current: 0, total: 0, batch: 0, totalBatches: 0 });
      setUseBackgroundProcessing(false);
      // 下载完成或取消后，清除所有选择
      setSelectedItems(new Set());
      setSelectAll(false);
    }
  };

  // Add to package
  const handleAddToPackages = () => {
    const selectedObjs = (Array.isArray(folderChildren[selectedFolderId]) ? folderChildren[selectedFolderId] : [])
      .filter(child => child && selectedItems.has(child.id));
    const selectedIds = selectedObjs.map(child => child.id);
    
    if (selectedIds.length === 0) {
      alert('Please select files or folders to add to package');
      return;
    }
    
    setShowAddToPackagesDialog(true);
  };

  // Upload to ACC
  const handleUploadToACC = () => {
    if (!selectedProject?.id || !selectedProject?.hubId) {
      toast({
        title: "Project not selected",
        description: "Please select a project first",
        variant: "destructive",
      });
      return;
    }

    if (selectedFolderId === 'root') {
      toast({
        title: "Invalid folder",
        description: "Please select a specific folder to upload files",
        variant: "destructive",
      });
      return;
    }

    // 构建正确的ACC URL格式
    const accBaseUrl = 'https://acc.autodesk.com';
    
    // 构建正确的ACC项目URL
    // 基于真实ACC URL格式：/build/files/projects/{projectId}
    // 移除项目ID前面的"b."前缀，因为ACC Web界面不需要
    const cleanProjectId = selectedProject.id.replace(/^b\./, '');
    let accUrl = `${accBaseUrl}/build/files/projects/${cleanProjectId}`;
    
    // 如果有选中的文件夹，使用真实的folderUrn
    if (selectedFolderId && selectedFolderId !== 'root') {
      // selectedFolderId 本身就是完整的folderUrn (例如: urn:adsk.wipprod:fs.folder:co.Cu2kUl8rSlqe6-SFzWh-Vg)
      accUrl += `?folderUrn=${encodeURIComponent(selectedFolderId)}&viewModel=detail&moduleId=folders`;
    } else {
      // 添加基本的查询参数以确保正确显示文件视图
      accUrl += `?viewModel=detail&moduleId=folders`;
    }
    
    // 直接跳转到ACC，不显示提示信息
    
    // 在新窗口中打开ACC页面
    window.open(accUrl, '_blank');
  };





  // 新增：搜索和筛选器组件
  const SearchAndFilterPanel = ({
    searchTerm,
    setSearchTerm,
    selectedVersion,
    setSelectedVersion,
    selectedFileType,
    setSelectedFileType,
    dateRange,
    setDateRange,
    availableVersions,
    availableFileTypes,
    currentFolderData,
    selectedFolderPath,
    searchScope
  }: {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    selectedVersion: string;
    setSelectedVersion: (version: string) => void;
    selectedFileType: string;
    setSelectedFileType: (type: string) => void;
            dateRange: DateRange | undefined;
      setDateRange: Dispatch<SetStateAction<DateRange | undefined>>;
    availableVersions: string[];
    availableFileTypes: string[];
    currentFolderData: any[];
    selectedFolderPath: string;
    searchScope: 'current' | 'global';
  }) => {
    const files = currentFolderData.filter(item => !item.isFolder);
    const folders = currentFolderData.filter(item => item.isFolder);
    
    // 搜索框已移至主组件，这里不再需要搜索相关逻辑
    
    const clearFilters = () => {
      setSearchTerm('');
      setSelectedVersion('all');
      setSelectedFileType('all');
      setDateRange(undefined);
    };
    
    // 在 Include Subfolders 模式下，只考虑搜索词，不考虑其他筛选器
    const hasActiveFilters = searchScope === 'global' 
      ? searchTerm
      : (searchTerm || selectedVersion !== 'all' || selectedFileType !== 'all' || dateRange);
    
    return (
      <div className="space-y-6">
        {/* Statistics */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Current Folder: {selectedFolderPath}</h3>
          <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
            <div>Folders: {folders.length}</div>
            <div>Files: {files.length}</div>
          </div>
        </div>
        
        {/* Search box moved to main component */}
        
        {/* Version filter - only show in current folder mode */}
        {searchScope === 'current' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">File Version</label>
            <select 
              value={selectedVersion} 
              onChange={(e) => {
                setSelectedVersion(e.target.value);
                // 立即触发筛选，不需要等待 debounce
                // useEffect 会自动响应状态变化并重新筛选
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Versions</option>
              {availableVersions.map(version => (
                <option key={version} value={version}>{version}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* File type filter - only show in current folder mode */}
        {searchScope === 'current' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">File Type</label>
            <select 
              value={selectedFileType} 
              onChange={(e) => {
                setSelectedFileType(e.target.value);
                // 立即触发筛选，不需要等待 debounce
                // useEffect 会自动响应状态变化并重新筛选
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              {availableFileTypes.map(type => (
                <option key={type} value={type}>{getFileTypeDisplayName(type)}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Date range filter - only show in current folder mode */}
        {searchScope === 'current' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Modified Date Range</label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal text-sm"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {dateRange?.from ? (
                  dateRange.to ? (
                    `${format(dateRange.from, 'M/d')} - ${format(dateRange.to, 'M/d')}`
                  ) : (
                    format(dateRange.from, 'M/d')
                  )
                ) : (
                  'Select Date'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              ref={datePickerRef}
              className="w-80 p-0" 
              align="start" 
              side="bottom" 
              sideOffset={4}
              onInteractOutside={(e) => {
                // 只有点击外部才允许关闭
                const target = e.target as Element;
                const isClickInsideDatePicker = target.closest('[data-date-picker-content]');
                
                if (isClickInsideDatePicker) {
                  e.preventDefault(); // 阻止关闭
                }
                // 点击外部时允许关闭，不需要额外处理
              }}
              onClick={(e) => {
                // 阻止内部点击冒泡到外部
                e.stopPropagation();
              }}
            >
              <div className="p-3" data-date-picker-content>
                <div>
                  {/* 自定义月份/年份选择器头部 */}
                  <div className="flex justify-center items-center space-x-2 py-2 border-b">
                    <button 
                      type="button"
                      onClick={() => {
                        const newMonth = new Date(calendarMonth);
                        newMonth.setMonth(newMonth.getMonth() - 1);
                        setCalendarMonth(newMonth);
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      ‹
                    </button>
                    <div className="flex items-center space-x-1">
                      <select 
                        className="text-sm border border-gray-300 rounded px-2 py-1 bg-blue-50"
                        value={calendarMonth.getMonth()}
                        onChange={(e) => {
                          const newDate = new Date(calendarMonth);
                          newDate.setMonth(parseInt(e.target.value));
                          setCalendarMonth(newDate);
                        }}
                      >
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i} value={i}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                      <select 
                        className="text-sm border border-gray-300 rounded px-2 py-1 bg-blue-50"
                        value={calendarMonth.getFullYear()}
                        onChange={(e) => {
                          const newDate = new Date(calendarMonth);
                          newDate.setFullYear(parseInt(e.target.value));
                          setCalendarMonth(newDate);
                        }}
                      >
                        {Array.from({length: 10}, (_, i) => (
                          <option key={i} value={2020 + i}>
                            {2020 + i}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button 
                      type="button"
                      onClick={() => {
                        const newMonth = new Date(calendarMonth);
                        newMonth.setMonth(newMonth.getMonth() + 1);
                        setCalendarMonth(newMonth);
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      ›
                    </button>
                  </div>
                  
                  {/* Calendar 组件 - 支持日期范围选择 */}
                  <CalendarComponent
                    mode="range"
                    month={calendarMonth}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range);
                      // 立即触发筛选，不需要等待
                      // useEffect 会自动响应状态变化并重新筛选
                      // 不自动关闭，让用户继续操作
                    }}
                    numberOfMonths={1}
                    className="rounded-md"
                    classNames={{
                      caption: "hidden", // 隐藏默认标题，使用自定义的
                      head_row: "flex",
                      head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem] text-center",
                      row: "flex w-full mt-2",
                      cell: "text-center text-sm p-0 relative w-8 h-8 flex items-center justify-center",
                      day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-gray-100",
                      day_selected: "bg-blue-500 text-white hover:bg-blue-600",
                      day_today: "bg-gray-100 text-gray-900",
                      day_range_start: "bg-blue-500 text-white rounded-l-md",
                      day_range_end: "bg-blue-500 text-white rounded-r-md",
                      day_range_middle: "bg-blue-100 text-blue-900",
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                  <div className="text-xs text-gray-500">
                    {dateRange?.from && dateRange?.to ? 
                      `${format(dateRange.from, 'M/d/yyyy')} - ${format(dateRange.to, 'M/d/yyyy')}` :
                      'Please select date range'
                    }
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                                                                                                              onClick={() => {
                        setDateRange(undefined);
                        // 不关闭 Popover，让用户继续操作
                      }}
                    className="text-blue-600 hover:text-blue-700 h-auto p-1 text-xs"
                  >
                    Clear Date
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          </div>
        )}
        
        {/* Clear filters button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            onClick={clearFilters}
            className="w-full text-red-600 border-red-300 hover:bg-red-50"
          >
            Clear All Filters
          </Button>
        )}
        

      </div>
    );
  };

  return (
    <>
      {!selectedProject ? (
        <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-lg border-2 border-dashed">
          <FolderClosed className="h-12 w-12 text-gray-400" />
          <p className="mt-4 text-center text-gray-600">Please select a project from the dropdown above to see its folders.</p>
        </div>
      ) : foldersLoading ? (
        <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-lg border-2 border-dashed">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-center text-gray-600">Loading folder structure...</p>
        </div>
      ) : (
        <div className="w-full h-[calc(100vh-140px)] flex flex-col">
          <Card className="flex-1 flex flex-col min-h-0 h-full border border-gray-300 rounded-lg mx-6 mt-4 relative z-30" style={{ marginBottom: '60px' }}>
            <CardContent className="flex-1 flex flex-col min-h-0 h-full p-0">
              <ResizablePanelGroup className="flex-1 min-h-0 w-full h-full" direction="horizontal">
                <ResizablePanel defaultSize={30} minSize={25} maxSize={50} className="pr-2 flex flex-col min-h-0 h-full">
                  <div className="flex-1 min-h-0 h-full flex flex-col overflow-y-auto overflow-x-hidden">
                      <div className="h-12 flex items-center justify-between mb-2 px-4 pt-4">
                      <div className="font-semibold text-lg">Folder Structure</div>
                    </div>
                    {/* Column Headers - 使用固定高度框架 */}
                    <div className="h-10 flex items-center px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-max">
                      <div className="flex-1 min-w-0">Folder Name</div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4">
                      <FolderItem
                        item={{
                          id: 'root',
                          name: 'Project Files',
                          isFolder: true,
                          children: (topFolders ?? []),
                        }}
                        depth={0}
                        expandedFolders={expandedFolders}
                        folderChildren={{ ...folderChildren, root: (topFolders ?? []) }}
                        onToggle={handleToggle}
                        onFileClick={() => {}}
                        loadingFolders={loadingFolders}
                        fileSizes={{}}
                        companies={companies}
                        selectedProject={selectedProject}
                        setShowLoginAlert={setShowLoginAlert}
                        onFolderTreeClick={handleFolderTreeClick}
                        selectedFolderId={selectedFolderId}
                        toast={toast}
                      />
                    </div>
                  </div>
                </ResizablePanel>
                <ResizableHandle className="bg-gray-300 w-px transition-colors duration-200 hover:bg-gray-500 cursor-col-resize self-stretch" />
                <ResizablePanel className="flex-1 min-w-0 flex flex-col min-h-0 h-full">
                  <div className="flex-1 min-h-0 h-full flex flex-col overflow-hidden min-w-full">
                    {/* Upload 和 Batch Actions 按钮区域 - 与左侧 Folder Structure 标题对齐 */}
                    <div className="h-12 flex items-center justify-between mb-2 px-4 pt-4">
                      <div className="flex items-center gap-2">
                       <Button 
                         size="sm" 
                         variant="outline" 
                         className="bg-green-600 hover:bg-green-700 text-white border-green-600 px-4 py-2 text-sm"
                         disabled={!selectedFolderId || selectedFolderId === 'root'}
                         onClick={handleUploadToACC}
                       >
                         <Upload className="h-4 w-4 mr-1" />
                         Upload
                       </Button>

                        {/* Batch Actions 按钮 - 只在有选中项时显示 */}
                       {(folderChildren[selectedFolderId]?.some(child => selectedItems.has(child.id)) || 
                         filteredData.some(child => selectedItems.has(child.id))) && (
                          <>
                         <DropdownMenu>
                           <DropdownMenuTrigger asChild>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="bg-white border-gray-300 hover:bg-gray-50 text-gray-700 border-2 font-medium shadow-sm"
                                  disabled={downloading}
                                >
                               Batch Actions
                             </Button>
                           </DropdownMenuTrigger>
                           <DropdownMenuContent>
                             <DropdownMenuItem onClick={handleBatchDownload} disabled={downloading}>
                                 <Download className="h-4 w-4 mr-2" />
                                  Batch Download
                             </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleAddToPackages} disabled={downloading}>
                               <Mail className="h-4 w-4 mr-2" />
                               Add to Package
                             </DropdownMenuItem>
                           </DropdownMenuContent>
                         </DropdownMenu>
                            
                            {/* Progress UI - 显示在 Batch Actions 右边 */}
                            {downloading && (
                              <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm h-8">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-blue-800">
                                    {useBackgroundProcessing 
                                      ? `Analyzing files for background processing...`
                                      : downloadProgress.totalBatches > 1 
                                        ? `Downloading batch ${downloadProgress.batch}/${downloadProgress.totalBatches} (${downloadProgress.current}/${downloadProgress.total} files)...`
                                        : `Downloading ${downloadProgress.current}/${downloadProgress.total} files...`
                                    }
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300 hover:bg-red-50 h-6 px-2 text-xs"
                                  onClick={() => {
                                    if (downloadAbortController) {
                                      downloadAbortController.abort();
                                    }
                                    setDownloading(false);
                                    setDownloadAbortController(null);
                                    setDownloadProgress({ current: 0, total: 0, batch: 0, totalBatches: 0 });
                                    setUseBackgroundProcessing(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            )}
                          </>
                       )}
                     </div>
                      <div className="font-semibold text-lg invisible">Folder Structure</div>
                    </div>
                    <div className="flex-1 min-h-0 min-w-0">
                      
                                             <FolderPreviewPanel
                         childrenList={debouncedSearchTerm || (searchScope === 'current' && (selectedVersion !== 'all' || selectedFileType !== 'all' || dateRange?.from || dateRange?.to)) ? filteredData : folderChildren[selectedFolderId] || []} // 搜索或筛选时使用筛选后的数据，否则使用原始数据
                         onFolderClick={handlePreviewFolderClick}
                         selectedProject={selectedProject}
                         setShowLoginAlert={setShowLoginAlert}
                         selectedItems={selectedItems}
                         setSelectedItems={setSelectedItems}
                         fileApprovals={fileApprovals}
                         setFileApprovals={setFileApprovals}
                         toast={toast}
                         onLoadMore={() => loadMoreFiles(selectedFolderId)}
                         onPageChange={handlePageChange}
                         pagination={debouncedSearchTerm ? {
                           page: folderPagination[selectedFolderId]?.page || 1,
                           total: filteredData.length,
                           hasMore: filteredData.length > 100,
                           loading: false
                         } : folderPagination[selectedFolderId]}
                         selectedFolderId={selectedFolderId}
                         folderPagination={folderPagination}
                         downloadingFiles={downloadingFiles}
                         setDownloadingFiles={setDownloadingFiles}
                         previewingFiles={previewingFiles}
                         setPreviewingFiles={setPreviewingFiles}
                         loadingFolders={loadingFolders}
                         colWidths={colWidths}
                         setColWidths={setColWidths}
                         handleItemSelect={handleItemSelect}
                         lastSelectedIndex={lastSelectedIndex}
                         setLastSelectedIndex={setLastSelectedIndex}
                         folderChildren={folderChildren}
                       />
                    </div>
                                      </div>
                  </ResizablePanel>
                  
                  {/* 筛选器面板 */}
                  {showFilterPanel && (
                    <>
                      <ResizableHandle className="bg-gray-300 w-px transition-colors duration-200 hover:bg-gray-500 cursor-col-resize self-stretch" />
                      <ResizablePanel defaultSize={25} minSize={20} maxSize={35} className="pl-2 flex flex-col min-h-0 h-full">
                        <div className="flex-1 min-h-0 h-full flex flex-col overflow-hidden">
                          <div className="flex items-center justify-between mb-4 mt-2 px-4 pt-4">
                            <div className="font-semibold text-lg">Filters</div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                // 关闭筛选器面板并清除所有搜索状态
                                setShowFilterPanel(false);
                                setLocalSearchTerm('');
                                setDebouncedSearchTerm('');
                                setFilteredData([]);
                                setIsSearchActive(false);
                                // 重置搜索结果的分页信息
                                setFolderPagination(prev => ({
                                  ...prev,
                                  [selectedFolderId]: {
                                    page: 1,
                                    total: (folderChildren[selectedFolderId] || []).filter(item => !item.isFolder).length,
                                    hasMore: false,
                                    loading: false
                                  }
                                }));
                              }}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              ×
                            </Button>
                          </div>
                          {/* 直接在主组件中的搜索框 - 避免子组件重新渲染问题 */}
                          <div className="px-4 mb-4 space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Search File Name</label>
                              <div className="relative">
                                <Input
                                  placeholder="Enter file name keywords..."
                                  value={localSearchTerm}
                                  onChange={(e) => {
                                    setLocalSearchTerm(e.target.value);
                                    // 当输入内容改变时，清除之前的搜索结果
                                    if (debouncedSearchTerm) {
                                      setDebouncedSearchTerm('');
                                      setFilteredData([]);
                                      setIsSearchActive(false);
                                    }
                                  }}
                                  className="w-full pr-8"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSearch();
                                    }
                                  }}
                                />
                                {/* 删除按钮 */}
                                {localSearchTerm && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setLocalSearchTerm('');
                                      setDebouncedSearchTerm('');
                                      setFilteredData([]);
                                      setIsSearchActive(false);
                                    }}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                              
                              {/* 搜索按钮 */}
                              <div className="flex space-x-2 mt-2">
                                <Button
                                  onClick={handleSearch}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                  size="sm"
                                  disabled={!localSearchTerm.trim() || isLoadingSubfolders}
                                >
                                  {isLoadingSubfolders ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                                      Loading...
                                    </>
                                  ) : (
                                    <>🔍 Search</>
                                  )}
                                </Button>
                                {debouncedSearchTerm && (
                                  <Button
                                    onClick={() => {
                                      setLocalSearchTerm('');
                                      setDebouncedSearchTerm('');
                                      setFilteredData([]);
                                      setIsSearchActive(false);
                                      // 重置搜索结果的分页信息
                                      setFolderPagination(prev => ({
                                        ...prev,
                                        [selectedFolderId]: {
                                          page: 1,
                                          total: (folderChildren[selectedFolderId] || []).filter(item => !item.isFolder).length,
                                          hasMore: false,
                                          loading: false
                                        }
                                      }));
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="px-3"
                                  >
                                    Clear
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {/* 搜索范围选择器 */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Search Scope</label>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSearchScope('current');
                                    // 切换搜索范围时清除搜索结果
                                    if (debouncedSearchTerm) {
                                      setDebouncedSearchTerm('');
                                      setFilteredData([]);
                                      setIsSearchActive(false);
                                    }
                                  }}
                                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                    searchScope === 'current'
                                      ? 'bg-blue-500 text-white border-blue-500'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Current Folder
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    console.log(`🎯 [SEARCH SCOPE] Setting to global, current:`, searchScope);
                                    setSearchScope('global');
                                    // 切换搜索范围时清除搜索结果
                                    if (debouncedSearchTerm) {
                                      setDebouncedSearchTerm('');
                                      setFilteredData([]);
                                      setIsSearchActive(false);
                                    }
                                    console.log(`🎯 [SEARCH SCOPE] After setting, should be global`);
                                  }}
                                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                    searchScope === 'global'
                                      ? 'bg-blue-500 text-white border-blue-500'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  Include Subfolders
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {searchScope === 'current' 
                                  ? 'Search only in current folder' 
                                  : 'Search in current folder and all subfolders (may take longer for deep folders)'}
                              </p>
                              
                              {/* 搜索提示和状态 */}
                              {localSearchTerm && !debouncedSearchTerm && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                  💡 Tip: Click "🔍 Search" to start searching. The system will automatically optimize search performance, the first search may be a bit slow.
                                </div>
                              )}
                              
                              {/* 搜索进度提示 */}
                              {isLoadingSubfolders && (
                                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                  <div className="flex items-center gap-2 text-yellow-800">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                                    <span className="font-medium">
                                      {searchScope === 'current' ? 'Searching current folder...' : 'Deep searching...'}
                                    </span>
                                  </div>
                                  <div className="mt-1 text-yellow-700">
                                    {searchScope === 'current' ? 
                                      'Searching only in current folder, faster speed' : 
                                      'Using intelligent algorithm to search all subfolders and build index in background for faster subsequent searches'}
                                  </div>
                                </div>
                              )}
                              
                              {/* 搜索结果提示 */}
                              {debouncedSearchTerm && filteredData.length > 0 && (
                                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                  ✅ Found {filteredData.length} matching files
                                  {searchScope === 'current' ? ' (current folder only)' : ' (including subfolders)'}
                                </div>
                              )}
                              
                              {debouncedSearchTerm && filteredData.length === 0 && !isLoadingSubfolders && (
                                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                                  ⚠️ No matching files found. Try:
                                  <ul className="mt-1 ml-2 list-disc list-inside">
                                    <li>Using shorter or more general search terms</li>
                                    <li>Checking spelling</li>
                                    <li>Trying file extensions (e.g., .pdf, .dwg)</li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-h-0 overflow-y-auto px-4">
                            <SearchAndFilterPanel
                              searchTerm={localSearchTerm}
                              setSearchTerm={setLocalSearchTerm}
                              selectedVersion={selectedVersion}
                              setSelectedVersion={setSelectedVersion}
                              selectedFileType={selectedFileType}
                              setSelectedFileType={setSelectedFileType}
                              dateRange={dateRange}
                              setDateRange={setDateRange}
                              availableVersions={availableVersions}
                              availableFileTypes={availableFileTypes}
                              currentFolderData={folderChildren[selectedFolderId] || []}
                              selectedFolderPath={selectedFolderId === 'root' ? 'Project Files' : 'Current Folder'}
                              searchScope={searchScope}
                            />
                          </div>
                        </div>
                      </ResizablePanel>
                    </>
                  )}
                </ResizablePanelGroup>
            </CardContent>
          </Card>
        </div>
      )}
      {/* 登录弹窗 */}
      {showLoginAlert && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 11000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: '40px 48px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            minWidth: 320,
            textAlign: 'center',
            position: 'relative',
          }}>
            <button
              style={{
                position: 'absolute',
                top: 12, right: 16,
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                color: '#888',
                cursor: 'pointer',
              }}
              onClick={() => setShowLoginAlert(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 32, marginTop: 8 }}>Please login to Autodesk</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Button
                onClick={() => {
                  const currentUrl = window.location.pathname + window.location.search;
                  const authUrl = `/api/auth/login?returnUrl=${encodeURIComponent(currentUrl)}`;
                  window.location.href = authUrl;
                }}
                className="w-full"
              >
                Login
              </Button>
              <button
                style={{
                  background: '#e5e7eb', color: '#222', border: 'none', borderRadius: 8,
                  fontSize: 18, fontWeight: 500, padding: '8px 32px', cursor: 'pointer',
                }}
                onClick={() => setShowLoginAlert(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Add to Packages Dialog */}
      <AddToPackagesDialog 
        open={showAddToPackagesDialog} 
        onOpenChange={setShowAddToPackagesDialog}
        selectedItems={Array.from(selectedItems)}
        folderChildren={folderChildren}
        selectedFolderId={selectedFolderId}
         selectedProject={selectedProject}
      />
    </>
  );
}

// Add to Packages Dialog Component
interface TenderPackage {
  id: string;
  name: string;
  description?: string;
  versionType?: string;
  submissionStatus: "draft" | "not_sent" | "sent" | "viewed" | "downloaded" | "tender_returned" | "won" | "lost" | "expired";
  createdAt: string;
}

interface AddToPackagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: string[];
  folderChildren: { [id: string]: FolderOrFile[] };
  selectedFolderId: string;
  selectedProject?: { id: string; hubId: string; name: string } | null;
}

function AddToPackagesDialog({ 
  open, 
  onOpenChange, 
  selectedItems, 
  folderChildren, 
  selectedFolderId,
  selectedProject
}: AddToPackagesDialogProps) {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    currentFile: '',
    status: 'idle' as 'idle' | 'processing' | 'completed' | 'error',
    message: ''
  });
  const [isCancelled, setIsCancelled] = useState(false);

  // Debug: Log when dialog opens
  useEffect(() => {
    if (open) {
      console.log('🔍 [AddToPackagesDialog] Dialog opened');
      console.log('🔍 [AddToPackagesDialog] selectedProject:', selectedProject);
      console.log('🔍 [AddToPackagesDialog] selectedProject?.id:', selectedProject?.id);
    }
  }, [open, selectedProject]);

  // Fetch available packages
  const { data: packages, isLoading: packagesLoading } = useQuery<TenderPackage[]>({
    queryKey: ['tender-packages', selectedProject?.id],
    queryFn: async () => {
      console.log('🔍 [AddToPackagesDialog] Starting API request...');
        const result = await apiRequest<TenderPackage[]>(`/api/tender-packages?projectId=${selectedProject?.id}&useOriginalStatus=true`);
      console.log('🔍 [AddToPackagesDialog] API response:', result);
      console.log('🔍 [AddToPackagesDialog] Response type:', typeof result);
      console.log('🔍 [AddToPackagesDialog] Response length:', result?.length);
      console.log('🔍 [AddToPackagesDialog] First package:', result?.[0]);
      console.log('🔍 [AddToPackagesDialog] First package keys:', Object.keys(result?.[0] || {}));
      console.log('🔍 [AddToPackagesDialog] First package status field:', {
        submissionStatus: result?.[0]?.submissionStatus
      });
      return result;
    },
    enabled: open && !!selectedProject?.id,
  });

  // Debug packages data when it changes
  useEffect(() => {
    if (packages) {
      console.log('🔍 [AddToPackagesDialog] Packages data changed:', packages);
      console.log('🔍 [AddToPackagesDialog] Packages count:', packages.length);
        console.log('🔍 [AddToPackagesDialog] All package statuses:', packages.map(pkg => ({ 
          name: pkg.name, 
          submission_status: (pkg as any).submission_status,
          submissionStatus: (pkg as any).submissionStatus 
        })));
        console.log('🔍 [AddToPackagesDialog] Draft packages:', packages.filter(pkg => (pkg as any).submission_status === 'draft'));
        console.log('🔍 [AddToPackagesDialog] Filter test:', packages.map(pkg => ({ 
          name: pkg.name, 
          submission_status: (pkg as any).submission_status, 
          submissionStatus: (pkg as any).submissionStatus,
          isDraft: (pkg as any).submission_status === 'draft' 
        })));
        console.log('🔍 [AddToPackagesDialog] Draft count check:', packages.filter(pkg => (pkg as any).submission_status === 'draft').length);
        console.log('🔍 [AddToPackagesDialog] Condition check:', {
          hasPackages: !!packages,
          packagesLength: packages?.length,
          draftCount: packages?.filter(pkg => (pkg as any).submission_status === 'draft').length,
          shouldShowSelect: !(!packages || packages.filter((pkg: TenderPackage) => (pkg as any).submission_status === 'draft').length === 0)
        });
    }
  }, [packages]);

  const { toast } = useToast();

  const handleCancel = () => {
    if (progress.status === 'processing') {
      setIsCancelled(true);
      setProgress(prev => ({
        ...prev,
        status: 'error',
        message: 'Operation cancelled by user',
        currentFile: ''
      }));
      toast({
        title: "Operation cancelled",
        description: "File addition process has been cancelled.",
        variant: "default",
      });
    } else {
      onOpenChange(false);
    }
  };

  const handleAddToPackage = async () => {
    if (!selectedPackage) {
      toast({
        title: "Please select a package",
        description: "You must choose a package before adding files",
        variant: "destructive",
      });
      return;
    }

    const selectedObjs = (Array.isArray(folderChildren[selectedFolderId]) ? folderChildren[selectedFolderId] : [])
      .filter(child => child && selectedItems.includes(child.id));
    
    if (selectedObjs.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select files to add to the package",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setProgress({
      current: 0,
      total: selectedObjs.length,
      currentFile: '',
      status: 'processing',
      message: 'Preparing to add files...'
    });

    try {
      const fileIds = selectedObjs.map(item => item.id);
      
      // Update progress - starting processing
      setProgress(prev => ({
        ...prev,
        message: `Processing ${selectedObjs.length} file(s)...`,
        currentFile: 'Initializing...'
      }));
      
      // Use apiRequest which already handles response parsing
      const { apiRequest } = await import('@/lib/queryClient');
      const result = await apiRequest(`/api/tender-packages/${selectedPackage}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          fileIds,
          projectId: selectedProject?.id // Add project ID to the request
        }),
      });

      console.log('Add to package result:', result);
      
      // Update progress - completed
      setProgress(prev => ({
        ...prev,
        current: prev.total,
        status: 'completed',
        message: 'Processing completed!',
        currentFile: ''
      }));
      
      // Show success message based on actual results
      if (result.success) {
        const addedCount = result.addedItems || 0;
        const duplicateCount = result.duplicateItems || 0;
        
        if (addedCount > 0) {
          toast({
            title: "✅ Files added successfully!",
            description: `${addedCount} file(s) added to the package${duplicateCount > 0 ? `, ${duplicateCount} duplicate(s) skipped` : ''}`,
            variant: "default",
          });
        } else if (duplicateCount > 0) {
          toast({
            title: "⚠️ No new files added",
            description: `All ${duplicateCount} selected file(s) already exist in the package`,
            variant: "default",
          });
        } else {
          toast({
            title: "ℹ️ No files added",
            description: "No files were added to the package. Please check the selection and try again.",
            variant: "default",
          });
        }
      } else {
        throw new Error(result.message || 'Failed to add files');
      }
      
      // Close dialog and refresh data after a short delay
      setTimeout(() => {
        onOpenChange(false);
        setSelectedPackage('');
        setProgress({
          current: 0,
          total: 0,
          currentFile: '',
          status: 'idle',
          message: ''
        });
        
        // Refresh package data to show updated contents
        window.dispatchEvent(new CustomEvent('refreshPackageData'));
      }, 1500);
    } catch (error) {
      console.error('Error adding files to package:', error);
      
      // Update progress - error
      setProgress(prev => ({
        ...prev,
        status: 'error',
        message: 'Error occurred while processing files',
        currentFile: ''
      }));
      
      toast({
        title: "Failed to add files",
        description: "An error occurred while adding files to package. Please try again.",
        variant: "destructive",
      });
      
      // Reset progress after error
      setTimeout(() => {
        setProgress({
          current: 0,
          total: 0,
          currentFile: '',
          status: 'idle',
          message: ''
        });
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedObjs = (Array.isArray(folderChildren[selectedFolderId]) ? folderChildren[selectedFolderId] : [])
    .filter(child => child && selectedItems.includes(child.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white shadow-xl border border-gray-200">
        <DialogHeader className="pb-4 border-b border-gray-100">
          <DialogTitle className="text-xl font-semibold text-gray-800">Add Files to Package</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-3">
              Selected {selectedObjs.length} items:
            </p>
            <div className="max-h-32 overflow-y-auto border border-blue-200 rounded bg-white p-3">
              {selectedObjs.map(item => (
                <div key={item.id} className="text-sm py-1 flex items-center">
                  <span className="mr-2 text-blue-600">
                    {item.isFolder ? '📁' : '📄'}
                  </span>
                  <span className="font-medium text-gray-700">{item.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">Select target package:</label>
            {packagesLoading ? (
              <div className="text-center py-6">
                <div className="flex items-center justify-center space-x-2 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 font-medium">Loading packages...</span>
                </div>
                <div className="text-sm text-gray-500">Fetching available packages for this project</div>
              </div>
            ) : (() => {
              // Flatten packages to include both main packages and addendums (sub-packages)
              const allPackagesFlat = packages?.flatMap((pkg: TenderPackage) => {
                const mainPkg = pkg;
                const addendums = (pkg as any).addendums || [];
                // Include main package and all its addendums in a flat list
                return [mainPkg, ...addendums];
              }) || [];
              
              // Filter to only include draft packages (both main and sub-packages)
              const draftPackages = allPackagesFlat.filter((pkg: any) => 
                (pkg.submission_status === 'draft' || pkg.submissionStatus === 'draft')
              );
              
              console.log('🔍 [AddToPackagesDialog] Render check:', {
                hasPackages: !!packages,
                packagesLength: packages?.length,
                allPackagesFlatLength: allPackagesFlat.length,
                draftPackagesLength: draftPackages.length,
                shouldShowSelect: draftPackages.length > 0
              });
              
              if (draftPackages.length === 0) {
                return (
                  <div className="text-center py-6 text-gray-500">
                    <p>No draft packages available</p>
                    <p className="text-sm text-gray-400 mt-1">Only packages with draft status can receive new files</p>
                    <div className="text-xs text-gray-300 mt-2">
                      Debug: Total packages: {packages?.length || 0}, 
                      All packages (including addendums): {allPackagesFlat.length},
                      Draft packages: {draftPackages.length}
                    </div>
                  </div>
                );
              }
              
              console.log('🔍 [AddToPackagesDialog] Rendering Select component with:', {
                draftPackagesLength: draftPackages.length,
                selectedPackage,
                draftPackages: draftPackages.map((pkg: any) => ({ 
                  id: pkg.id, 
                  name: pkg.name, 
                  packageNumber: pkg.package_number || pkg.packageNumber,
                  isAddendum: pkg.isAddendum || pkg.is_addendum,
                  status: pkg.submission_status || pkg.submissionStatus 
                }))
              });
              
              return (
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Package
                  </label>
                  <Select 
                    value={selectedPackage} 
                    onValueChange={(value) => {
                      console.log('🔍 [AddToPackagesDialog] Select changed:', value);
                      setSelectedPackage(value);
                    }}
                    disabled={draftPackages.length === 0}
                  >
                    <SelectTrigger className="w-full border-2 border-gray-300 bg-white hover:border-blue-400 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <SelectValue placeholder="Select Package" />
                    </SelectTrigger>
                    <SelectContent className="z-[10002] max-h-[300px]">
                      {draftPackages.map((pkg: any) => {
                        const packageNumber = pkg.package_number || pkg.packageNumber || 'N/A';
                        const isAddendum = pkg.isAddendum || pkg.is_addendum;
                        const displayName = isAddendum ? `${packageNumber} (Addendum) - ${pkg.name}` : `${packageNumber} - ${pkg.name}`;
                        return (
                          <SelectItem key={pkg.id} value={pkg.id}>
                            {displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-gray-500 mt-1">
                    Available packages: {draftPackages.length} (including addendums)
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Progress UI */}
        {progress.status !== 'idle' && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">
                {progress.status === 'processing' && 'Adding Files to Package...'}
                {progress.status === 'completed' && '✅ Processing Complete!'}
                {progress.status === 'error' && '❌ Processing Failed'}
              </h4>
              <span className="text-xs text-gray-500">
                {progress.current}/{progress.total} files
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  progress.status === 'completed' ? 'bg-green-500' :
                  progress.status === 'error' ? 'bg-red-500' :
                  'bg-blue-500'
                }`}
                style={{ 
                  width: progress.total > 0 ? `${(progress.current / progress.total) * 100}%` : '0%' 
                }}
              />
            </div>
            
            {/* Status Message */}
            <div className="text-sm text-gray-600 mb-2">
              {progress.message}
            </div>
            
            {/* Current File */}
            {progress.currentFile && (
              <div className="text-xs text-gray-500 truncate">
                Processing: {progress.currentFile}
              </div>
            )}
            
            {/* Loading Animation */}
            {progress.status === 'processing' && (
              <div className="flex items-center space-x-2 mt-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-xs text-gray-500">Please wait while files are being processed...</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="pt-4 border-t border-gray-100">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={progress.status === 'completed'}
            className="border-gray-300 hover:bg-gray-50"
          >
            {progress.status === 'processing' ? 'Cancel Operation' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleAddToPackage} 
            disabled={
              isLoading || 
              !selectedPackage || 
              !packages || 
              packages.filter((pkg: TenderPackage) => {
                // Only allow adding files to packages that:
                // 1. Are not locked
                // 2. Are not archived
                // 3. Are in draft or not_sent status
                const isLocked = (pkg as any).isLocked || false;
                const isArchived = (pkg as any).status === 'archived';
                const allowedStatuses = ['draft', 'not_sent'];
                return !isLocked && !isArchived && allowedStatuses.includes(pkg.submissionStatus);
              }).length === 0
            }
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {progress.status === 'processing' ? 'Processing...' : 
             progress.status === 'completed' ? 'Complete!' :
             progress.status === 'error' ? 'Retry' :
             isLoading ? 'Adding...' : 'Add to Package'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}